// react and js
import { React, useState, useRef } from "react";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select, CardMedia, Link } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalAlertWarning,
  ModalAlertError,
} from "../../../theme/styled";

// utils
import { TxtDataToModuleData } from "../../../utils/moduleConversion";

/*
####### Example Trading Strategy ########
Module8103		OPEN  Example A1: MACD Long trading
Module8882		OPEN  Example A2: MACD Short trading
Module7824		OPEN  Example A3: Bollinger bands trading
Module2803		OPEN  Example A4: News trading of high impact calendar events
Module1988		OPEN  Example A5: News trading of regional high impact calendar events
Module1874		OPEN  Example A6: Avoiding high impact calendar events
Module2647		OPEN  Example A7: One chart analyzer for signal generation
Module8103 OR Module8882 OR Module7824 OR Module2803 OR Module1988 OR Module1874 OR Module2647

####### Demonstration of Functionality - Timing ########
Module7761		OPEN  Example B1: Trading at specific hours
Module1179		OPEN  Example B2: Trading at specific days of week
Module2413		OPEN  Example B3: Trading at specific days of month
Module2780		OPEN  Example B4: Intraday trading mode with forced closures at end of day
Module7156		OPEN  Example B5: Automatic signal shifting depending on daylight saving time (DST)
Module7761 OR Module1179 OR Module2413 OR Module2780 OR Module7156

####### Demonstration of Functionality - Stop Loss Trailing ########
Module1460		OPEN  Example C1: Average true range stop loss trailing
Module1621		OPEN  Example C2: First breakeven, then average true range stop loss trailing
Module8396		OPEN  Example C3: First breakeven, then average true range, then market stop loss trailing
Module1460 OR Module1621 OR Module8396

####### Demonstration of Functionality - Initial Stop Loss and Initial Take Profit ########
Module4670		OPEN  Example D1: Fixed pips distance as initial stop loss and initial take profit
Module8563		OPEN  Example D2: Indicator value for initial stop loss and initial take profit
Module8362		OPEN  Example D3: Indicator value for initial stop loss, stop loss trailing, initial take profit and dynamic take profit
Module4406		OPEN  Example D4: Support and resistance for initial stop loss and initial take profit
Module2412		OPEN  Example D5: Support and resistance for initial stop loss, stop loss trailing, initial take profit and dynamic take profit
Module9923		OPEN  Example D6: Candle pattern search used for initial stop loss and initial take profit
Module3274		OPEN  Example D7: Chart pattern search used for initial stop loss and initial take profit
Module3122		OPEN  Example D8: Fibonacci pattern search used for initial stop loss and initial take profit
Module4670 OR Module8563 OR Module8362 OR Module4406 OR Module2412 OR Module9923 OR Module3274 OR Module3122

####### Demonstration of Functionality - Trade Splitting ########
Module3521		OPEN  Example E1: Two trades with a pending order
Module4421		OPEN  Example E2: Three trades
Module9338		OPEN  Example E3: Four trades
Module3521 OR Module4421 OR Module9338

####### Demonstration of Functionality - Performance and drawdown monitoring ########
Module5238		OPEN  Example F1: Temporary disabling of trading due to bad performance
Module5255		OPEN  Example F2: Permanent disabling of trading due to bad performance
Module6743		OPEN  Example F3: Temporary disabling of trading due to bad drawdown
Module9352		OPEN  Example F4: Permanent disabling of trading due to bad drawdown
Module5238 OR Module5255 OR Module6743 OR Module9352

####### Demonstration of Functionality - Pending orders ########
Module9763		OPEN  Example G1: Buy stop and StdDev pending price
Module2003		OPEN  Example G2: Buy limit and StdDev pending price
Module6172		OPEN  Example G3: Sell stop and StdDev pending price
Module9758		OPEN  Example G4: Sell limit and StdDev pending price
Module3851		OPEN  Example G5: Buy stop and rate of change pending price
Module2994		OPEN  Example G6: Buy stop and indicator value for pending price
Module9677		OPEN  Example G7: Buy stop and support and resistance for pending price
Module9667		OPEN  Example G8: Buy stop and price dependent activation (hysteresis min)
Module9222		OPEN  Example G9: Buy stop and price dependent cancellation (hysteresis max)
Module9763 OR Module2003 OR Module6172 OR Module9758 OR Module3851 OR Module2994 OR Module9677 OR Module9667 OR Module9222

####### Demonstration of Functionality - News trading ########
Module6638		OPEN  Example H1: Events filtered by title
Module5742		OPEN  Example H2: Events filtered by relation
Module6638 OR Module5742

####### Demonstration of Functionality - Indicators ########
Module2915		OPEN  Example I1: One indicator line used
Module3421		OPEN  Example I2: One indicator line with time exclusion used
Module3232		OPEN  Example I3: Two indicator lines combined
Module9577		OPEN  Example I4: Four indicator lines combined
Module1529		OPEN  Example I5: Using price as indicator
Module2915 OR Module3421 OR Module3232 OR Module9577 OR Module1529

####### Demonstration of Functionality - Oscillators ########
Module2134		OPEN  Example J1: One oscillator line used as signal
Module9327		OPEN  Example J2: Two oscillator lines combined as signal
Module2321		OPEN  Example J3: Using volume as oscillator
Module2134 OR Module9327 OR Module2321

####### Demonstration of Functionality - Trade Basics ########
Module9543		OPEN  Example K1: Multiple open trades allowed
Module1510		OPEN  Example K2: Signals used for opening and closing trades
Module3795		OPEN  Example K3: Signal OR combination
Module1454		OPEN  Example K4: Forced waiting time between trades
Module9543 OR Module1510 OR Module3795 OR Module1454

####### Demonstration of Functionality - Visualization of signals ########
Module8718		OPEN  Example L1: MACD Long signal only
Module6800		OPEN  Example L2: MACD Short signal only
Module8718 OR Module6800

####### Demonstration of Functionality - Volume Calculation ########
Module2377		OPEN  Example M1: Indicator value for volume calculation with external indicator
Module2377

####### Demonstration of Functionality - Search Engines ########
Module4950		OPEN  Example N1: Non-traded signals generated by candle search
Module3322		OPEN  Example N2: Non-traded signals generated by pattern search
Module1788		OPEN  Example N3: Non-traded signals generated by fibonacci search
Module4950 OR Module3322 OR Module1788

####### Demonstration of Functionality - Chart Analyzer ########
Module1865		OPEN  Example O1: Extended swing analysis
Module7870		OPEN  Example O2: Drawing of chart analyzer panel
Module1452		OPEN  Example O3: Exclusion of support and resistance when forwarding
Module1192		OPEN  Example O4: Two chart analyzers for signal generation
Module1865 OR Module7870 OR Module1452 OR Module1192

####### Demonstration of Functionality - Data Feedback ########
Module9020		OPEN  Example P1: All feedback features enabled
Module9020

####### Demonstration of Functionality - Multi-Symbol Trading ########
Module5949		OPEN  Example Q1: Symbol mapping for custom symbols
Module5668		OPEN  Example Q2: Usage of multiple symbols and symbol feedback (EURUSD and USDCHF)
Module3457		OPEN  Example Q3: Correlation coefficient between two symbols (EURUSD and USDCHF)
Module5949 OR Module5668 OR Module3457

####### Demonstration of Functionality - External Python Script integration ########
Module2258		OPEN  Example R1: Signal from custom oscillator and external Python script
Module9553		OPEN  Example R2: Parameter forwarding and feedback parsing with custom indicator and external Python script
Module2258 OR Module9553

####### Demonstration of Functionality - Others ########
Module6030		OPEN  Example S1: Drawing of second timeframe in chart
Module9898		OPEN  Example S2: Drawing of unequal symbols
Module6030 OR Module9898
*/

// Example modules data
const exampleModules = [
  // ----------------------------------------------------------------------
  // Example Trading Strategy
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8103.txt",
    name: "Example Trading Strategy | Example A1: MACD Long trading",
    description: (
      <>
        This example demonstrates a fundamental long trading strategy using the MACD (Moving Average Convergence Divergence) indicator. The strategy
        opens buy positions when the MACD main line crosses above the signal line, indicating potential upward momentum. The Standard Deviation
        indicator calculates both the initial stop loss, providing dynamic risk management based on market volatility.
        <br />
        <br />
        The system uses a risk ratio of 1.40 for the take profit level, meaning the potential profit target is 40% larger than the risk taken. This
        creates a favorable risk-reward balance for long-term profitability. The forward adjustment feature helps position the stop loss at an
        appropriate distance from the entry price, preventing premature exits during normal market fluctuations.
        <br />
        <br />
        This configuration works well across different timeframes and currency pairs. Beginners can use this example to understand how technical
        indicators generate trading signals and how proper risk management protects capital while allowing profits to run.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8103/example_image_1.png",
        description: "Expected behavior:",
        height: "581px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8882.txt",
    name: "Example Trading Strategy | Example A2: MACD Short trading",
    description: (
      <>
        This example showcases a short trading strategy that mirrors the long MACD approach but operates in the opposite direction. The strategy opens
        sell positions when the MACD signal line crosses above the main line, suggesting downward price movement ahead. This reversal of the crossing
        condition adapts the indicator for bearish market conditions.
        <br />
        <br />
        Similar to the long version, the Standard Deviation indicator manages risk by determining stop loss placement with a forward adjustment of
        3.50. This multiplier ensures the stop loss stays far enough from the entry to avoid market noise. The take profit uses the same 1.40 risk
        ratio, maintaining consistent risk-reward principles regardless of trade direction.
        <br />
        <br />
        Short trading requires careful attention to market conditions since many assets have an upward bias over time. This example helps traders
        understand how the same indicators can generate opposite signals by adjusting the relationship conditions between indicator lines.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Short</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8882/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module7824.txt",
    name: "Example Trading Strategy | Example A3: Bollinger bands trading",
    description: (
      <>
        This example introduces Bollinger Bands trading, a popular strategy that identifies potential entry points when price breaks through
        statistical boundaries. The system generates long signals when the opening price crosses below the lower Bollinger Band, suggesting an
        oversold condition that may reverse upward. All three Bollinger Band lines are displayed on the chart for visual confirmation.
        <br />
        <br />
        The strategy uses a fixed 100 pip stop loss, making risk calculation straightforward for position sizing. The take profit maintains the
        standard 1.40 risk ratio, targeting 140 pips of profit. This fixed approach works particularly well in ranging markets where price tends to
        bounce between the bands.
        <br />
        <br />
        Bollinger Bands adapt to market volatility automatically, widening during volatile periods and narrowing during calm markets. This
        self-adjusting nature makes the strategy suitable for various market conditions.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Main)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Main</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Upper)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Upper</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Lower)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Lower</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (PRICE)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>PRICE</b>
              </li>
              <li>
                Series = <b>Open</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>4</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (PRICE Open & BB Lower)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>PRICE (Open)</b>
              </li>
              <li>
                Input indicator 2 = <b>BB (Lower)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Rate of change</b>
              </li>
              <li>
                Rate change = <b>0.00100000</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module7824/example_image_1.png",
        description: "Expected behavior:",
        height: "535px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2803.txt",
    name: "Example Trading Strategy | Example A4: News trading of high impact calendar events",
    description: (
      <>
        This example demonstrates news trading for high-impact economic events across all global currencies. The strategy opens positions 15 minutes
        before and after major news releases, capitalizing on increased volatility during these periods. The Average True Range (ATR) indicator
        provides adaptive stop loss levels that adjust to current market volatility.
        <br />
        <br />
        A unique feature is the trailing stop loss that activates immediately at trade opening, continuously adjusting based on ATR values. This
        protects profits as price moves favorably while giving trades room to develop. The forward adjustment of 2.50 multiplies the ATR value,
        creating appropriate spacing for volatile news conditions.
        <br />
        <br />
        High-impact events like interest rate decisions and employment reports often create significant price movements. This strategy positions
        traders to capture these moves while managing risk through dynamic stop losses that respond to changing volatility levels during news
        releases.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (ATR)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Drawing in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
              <li>
                Forward adjustment = <b>2.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Draw table with upcoming events = <b>Off</b>
              </li>
              <li>
                Pre event signal time = <b>900</b>
              </li>
              <li>
                Post event signal time = <b>900</b>
              </li>
              <li>
                Signals for ALL regions = <b>On</b>
              </li>
              <li>
                Signals for AUD region = <b>On</b>
              </li>
              <li>
                Signals for BRL region = <b>On</b>
              </li>
              <li>
                Signals for CAD region = <b>On</b>
              </li>
              <li>
                Signals for CHF region = <b>On</b>
              </li>
              <li>
                Signals for CNY region = <b>On</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>On</b>
              </li>
              <li>
                Signals for HKD region = <b>On</b>
              </li>
              <li>
                Signals for INR region = <b>On</b>
              </li>
              <li>
                Signals for JPY region = <b>On</b>
              </li>
              <li>
                Signals for KRW region = <b>On</b>
              </li>
              <li>
                Signals for MXN region = <b>On</b>
              </li>
              <li>
                Signals for NOK region = <b>On</b>
              </li>
              <li>
                Signals for NZD region = <b>On</b>
              </li>
              <li>
                Signals for SEK region = <b>On</b>
              </li>
              <li>
                Signals for SGD region = <b>On</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>On</b>
              </li>

              <li>
                Signals for undefined importance = <b>Off</b>
              </li>
              <li>
                Signals for low importance = <b>Off</b>
              </li>
              <li>
                Signals for moderate importance = <b>Off</b>
              </li>
              <li>
                Signals for high importance = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2803/example_image_1.png",
        description: "Expected behavior:",
        height: "459px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1988.txt",
    name: "Example Trading Strategy | Example A5: News trading of regional high impact calendar events",
    description: (
      <>
        This example focuses on regional news trading, specifically targeting EUR and USD economic events. Unlike the previous news trading example,
        this strategy filters events by currency region, allowing traders to specialize in specific market pairs like EURUSD. The 15-minute timeframe
        recommendation helps capture quick price movements following announcements.
        <br />
        <br />
        The configuration maintains the same ATR-based risk management system with trailing stops activated from position opening. By limiting signals
        to EUR and USD events only, traders avoid overwhelming signal generation and can focus on understanding how specific economies affect their
        chosen currency pair.
        <br />
        <br />
        Regional filtering proves especially useful for traders who follow particular economic zones closely. European Central Bank and Federal
        Reserve announcements typically have the strongest impact on EURUSD, making this targeted approach more efficient than monitoring all global
        events simultaneously.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (ATR)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Drawing in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
              <li>
                Forward adjustment = <b>2.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Draw table with upcoming events = <b>Off</b>
              </li>
              <li>
                Pre event signal time = <b>900</b>
              </li>
              <li>
                Post event signal time = <b>900</b>
              </li>
              <li>
                Signals for ALL regions = <b>Off</b>
              </li>
              <li>
                Signals for AUD region = <b>Off</b>
              </li>
              <li>
                Signals for BRL region = <b>Off</b>
              </li>
              <li>
                Signals for CAD region = <b>Off</b>
              </li>
              <li>
                Signals for CHF region = <b>Off</b>
              </li>
              <li>
                Signals for CNY region = <b>Off</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>Off</b>
              </li>
              <li>
                Signals for HKD region = <b>Off</b>
              </li>
              <li>
                Signals for INR region = <b>Off</b>
              </li>
              <li>
                Signals for JPY region = <b>Off</b>
              </li>
              <li>
                Signals for KRW region = <b>Off</b>
              </li>
              <li>
                Signals for MXN region = <b>Off</b>
              </li>
              <li>
                Signals for NOK region = <b>Off</b>
              </li>
              <li>
                Signals for NZD region = <b>Off</b>
              </li>
              <li>
                Signals for SEK region = <b>Off</b>
              </li>
              <li>
                Signals for SGD region = <b>Off</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>Off</b>
              </li>

              <li>
                Signals for undefined importance = <b>Off</b>
              </li>
              <li>
                Signals for low importance = <b>Off</b>
              </li>
              <li>
                Signals for moderate importance = <b>Off</b>
              </li>
              <li>
                Signals for high importance = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1988/example_image_1.png",
        description: "Expected behavior:",
        height: "563px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1874.txt",
    name: "Example Trading Strategy | Example A6: Avoiding high impact calendar events",
    description: (
      <>
        This example illustrates defensive news trading by avoiding high-impact events rather than trading them. The signal inversion feature reverses
        the logic, generating entry signals only during calm periods without major news. The extended 2-hour buffer before and after events ensures
        complete avoidance of news-related volatility.
        <br />
        <br />
        The MACD crossing system provides the primary entry signals during these quiet periods. The ATR-based stop loss and trailing system adapt to
        lower volatility conditions typical of non-news periods. This approach suits conservative traders who prefer predictable market conditions
        over potentially profitable but risky news events.
        <br />
        <br />
        By combining technical analysis with news filtering, this strategy captures steady trends while avoiding unpredictable spikes. The visual
        signal lines help traders see exactly when the system blocks trades due to upcoming events, providing transparency in the decision-making
        process.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (ATR)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Drawing in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
              <li>
                Forward adjustment = <b>2.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Signal visualization = <b>On</b>
              </li>
              <li>
                Draw table with upcoming events = <b>Off</b>
              </li>
              <li>
                Signal inversion = <b>On</b>
              </li>
              <li>
                Pre event signal time = <b>7200</b>
              </li>
              <li>
                Post event signal time = <b>7200</b>
              </li>
              <li>
                Signals for ALL regions = <b>On</b>
              </li>
              <li>
                Signals for AUD region = <b>On</b>
              </li>
              <li>
                Signals for BRL region = <b>On</b>
              </li>
              <li>
                Signals for CAD region = <b>On</b>
              </li>
              <li>
                Signals for CHF region = <b>On</b>
              </li>
              <li>
                Signals for CNY region = <b>On</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>On</b>
              </li>
              <li>
                Signals for HKD region = <b>On</b>
              </li>
              <li>
                Signals for INR region = <b>On</b>
              </li>
              <li>
                Signals for JPY region = <b>On</b>
              </li>
              <li>
                Signals for KRW region = <b>On</b>
              </li>
              <li>
                Signals for MXN region = <b>On</b>
              </li>
              <li>
                Signals for NOK region = <b>On</b>
              </li>
              <li>
                Signals for NZD region = <b>On</b>
              </li>
              <li>
                Signals for SEK region = <b>On</b>
              </li>
              <li>
                Signals for SGD region = <b>On</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>On</b>
              </li>
              <li>
                Signals for undefined importance = <b>Off</b>
              </li>
              <li>
                Signals for low importance = <b>Off</b>
              </li>
              <li>
                Signals for moderate importance = <b>Off</b>
              </li>
              <li>
                Signals for high importance = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1874/example_image_1.png",
        description: "Expected behavior:",
        height: "559px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2647.txt",
    name: "Example Trading Strategy | Example A7: One chart analyzer for signal generation",
    description: (
      <>
        This example introduces the chart analyzer for volatility-based signal generation. The analyzer examines the last 50 bars to determine market
        volatility levels, only generating signals during high volatility periods when price movements offer better profit potential. This intelligent
        filtering reduces false signals during quiet market conditions.
        <br />
        <br />
        The fixed 200 pip stop loss provides consistent risk management, while the 1.40 risk ratio maintains favorable reward potential. The chart
        analyzer's visual indicators help traders understand current market conditions at a glance, showing whether volatility meets the threshold for
        trading.
        <br />
        <br />
        This approach works particularly well for momentum traders who thrive in active markets. By waiting for high volatility confirmation, the
        strategy avoids the choppy, directionless price action common in ranging markets, focusing instead on periods with clear directional movement.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Signal visualization = <b>On</b>
              </li>
              <li>
                Analyzed width = <b>50</b>
              </li>
              <li>
                Signal if volatility is low = <b>Off</b>
              </li>
              <li>
                Signal if volatility is middle = <b>Off</b>
              </li>
              <li>
                Signal if volatility is high = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Fix pips</b>
              </li>
              <li>
                Fix pips = <b>200</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2647/example_image_1.png",
        description: "Expected behavior:",
        height: "541px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Timing
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module7761.txt",
    name: "Demonstration of Functionality | Timing | Example B1: Trading at specific hours",
    description: (
      <>
        This example demonstrates time-based trading restrictions, limiting activity to specific hours of the day. The strategy only allows trades
        between 6-11 AM and 2-7 PM, avoiding overnight sessions and lunch hours when liquidity may be lower. Visual timing indicators on the chart
        clearly show active and restricted periods.
        <br />
        <br />
        The SMA and EMA crossing system provides entry signals, but only during permitted hours. Even if perfect signals occur outside these windows,
        the system ignores them completely. The Standard Deviation indicator manages stop losses with its proven forward adjustment system.
        <br />
        <br />
        Fixed 0.10 lot sizing simplifies position management for beginners. Time restrictions help traders focus on the most liquid market sessions
        when spreads are tighter and price action more reliable. This disciplined approach prevents impulsive trading during unfavorable market
        conditions.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Timing
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Visualize timing = <b>On</b>
              </li>
              <li>
                Trading hours = <b>6-11;14-19</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module7761/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1179.txt",
    name: "Demonstration of Functionality | Timing | Example B2: Trading at specific days of week",
    description: (
      <>
        This example extends timing control to specific weekdays, only allowing trades on Wednesdays and Thursdays. Market research often shows
        certain weekdays have different characteristics - Mondays can be unpredictable, Fridays see position squaring, while mid-week offers more
        stable trends. The visual timing system highlights permitted trading days clearly.
        <br />
        <br />
        The technical setup remains consistent with moving average crossings for signals and Standard Deviation for stop losses. By limiting trading
        to two days, the strategy reduces market exposure while potentially improving win rates by focusing on statistically favorable periods.
        <br />
        <br />
        This approach suits part-time traders who cannot monitor markets daily. It also helps maintain discipline by preventing overtrading, a common
        problem for enthusiastic beginners. The fixed lot size and risk ratio keep money management simple and consistent.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Timing
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Visualize timing = <b>On</b>
              </li>
              <li>
                Trading days of week = <b>Wednesday-Thursday</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1179/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2413.txt",
    name: "Demonstration of Functionality | Timing | Example B3: Trading at specific days of month",
    description: (
      <>
        This example restricts trading to specific days of the month, specifically days 10 through 20. This mid-month period often shows different
        characteristics than month-end or month-beginning when institutional rebalancing occurs. The visual timing display clearly marks active
        trading periods on the chart.
        <br />
        <br />
        Many traders notice patterns related to monthly cycles - salary payments, option expirations, and quarterly reports all create recurring
        market behaviors. By focusing on the middle third of each month, this strategy avoids these potentially disruptive events while capturing
        steady mid-month trends.
        <br />
        <br />
        The technical indicators remain unchanged, proving that timing filters can enhance any strategy without modifying core signal logic. This
        modular approach allows traders to test different timing windows while keeping their proven entry and exit rules intact.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Timing
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Visualize timing = <b>On</b>
              </li>
              <li>
                Trading days = <b>10-20</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2413/example_image_1.png",
        description: "Expected behavior:",
        height: "625px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2780.txt",
    name: "Demonstration of Functionality | Timing | Example B4: Intraday trading mode with forced closures at end of day",
    description: (
      <>
        This example implements intraday trading mode with forced closures at day's end. Positions can only open between 9 AM and 6 PM, with all
        trades automatically closed by 9 PM regardless of profit or loss. This prevents overnight risk and swap charges while ensuring clean starts
        each trading day.
        <br />
        <br />
        The forced closure feature distinguishes this from simple time restrictions. Even profitable trades must close by the deadline, preventing the
        temptation to hold positions overnight. This discipline proves valuable for day traders who must maintain strict separation between trading
        days.
        <br />
        <br />
        Visual timing indicators show three zones: transparent for full trading, orange for exit-only periods, and red for forced closure times. This
        clear visualization helps traders understand exactly when the system will act, removing uncertainty from the trading process.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Timing
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Visualize timing = <b>On</b>
              </li>
              <li>
                Intraday mode = <b>09:00:00</b>
              </li>
              <li>
                Start intraday trading = <b>18:00:00</b>
              </li>
              <li>
                Deadline intraday trading = <b>21:00:00</b>
              </li>
              <li>
                Intraday mode = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2780/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module7156.txt",
    name: "Demonstration of Functionality | Timing | Example B5: Automatic signal shifting depending on daylight saving time (DST)",
    description: (
      <>
        This example addresses daylight saving time (DST) adjustments, automatically shifting trading hours when DST changes occur. The configuration
        shows complex DST rules with different start and end dates, demonstrating how the system adapts to time zone variations. This ensures
        strategies maintain consistent market timing throughout the year.
        <br />
        <br />
        The news-based signals combine with hourly restrictions to create a sophisticated timing system. When DST shifts occur, the entire schedule
        adjusts automatically, maintaining alignment with actual market sessions rather than clock time. This prevents strategies from trading at
        unintended times after DST changes.
        <br />
        <br />
        Multiple screenshots show the system operating correctly across different DST periods. This advanced feature proves essential for strategies
        dependent on specific market sessions, especially when trading instruments from different time zones simultaneously.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Signals for ALL regions = <b>Off</b>
              </li>
              <li>
                Signals for AUD region = <b>Off</b>
              </li>
              <li>
                Signals for BRL region = <b>Off</b>
              </li>
              <li>
                Signals for CAD region = <b>Off</b>
              </li>
              <li>
                Signals for CHF region = <b>Off</b>
              </li>
              <li>
                Signals for CNY region = <b>Off</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>Off</b>
              </li>
              <li>
                Signals for HKD region = <b>Off</b>
              </li>
              <li>
                Signals for INR region = <b>Off</b>
              </li>
              <li>
                Signals for JPY region = <b>Off</b>
              </li>
              <li>
                Signals for KRW region = <b>Off</b>
              </li>
              <li>
                Signals for MXN region = <b>Off</b>
              </li>
              <li>
                Signals for NOK region = <b>Off</b>
              </li>
              <li>
                Signals for NZD region = <b>Off</b>
              </li>
              <li>
                Signals for SEK region = <b>Off</b>
              </li>
              <li>
                Signals for SGD region = <b>Off</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>Off</b>
              </li>

              <li>
                Signals for undefined importance = <b>Off</b>
              </li>
              <li>
                Signals for low importance = <b>On</b>
              </li>
              <li>
                Signals for moderate importance = <b>On</b>
              </li>
              <li>
                Signals for high importance = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Timing
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Visualize timing = <b>On</b>
              </li>
              <li>
                Trading hours = <b>9-19</b>
              </li>

              <li>
                DST summer: Shifted minutes = <b>60</b>
              </li>
              <li>
                DST summer: Start = <b>Mar 2nd Sun 02:00:00</b>
              </li>
              <li>
                DST summer: End = <b>Apr 1st Sun 02:00:00</b>
              </li>
              <li>
                DST winter: Shifted minutes = <b>-60</b>
              </li>
              <li>
                DST winter: Start = <b>Oct 2nd Sun 02:00:00</b>
              </li>
              <li>
                DST winter: End = <b>Nov 1st Sun 02:00:00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module7156/example_image_1.png",
        description: "Expected behavior 1:",
        height: "629px",
      },
      {
        src: "/illustrations/converted/examples/Module7156/example_image_2.png",
        description: "Expected behavior 2:",
        height: "629px",
      },
      {
        src: "/illustrations/converted/examples/Module7156/example_image_3.png",
        description: "Expected behavior 3:",
        height: "629px",
      },
      {
        src: "/illustrations/converted/examples/Module7156/example_image_4.png",
        description: "Expected behavior 4:",
        height: "629px",
      },
      {
        src: "/illustrations/converted/examples/Module7156/example_image_5.png",
        description: "Expected behavior 5:",
        height: "629px",
      },
      {
        src: "/illustrations/converted/examples/Module7156/example_image_6.png",
        description: "Expected behavior 6:",
        height: "629px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Stop Loss Trailing
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1460.txt",
    name: "Demonstration of Functionality | Stop Loss Trailing | Example C1: Average true range stop loss trailing",
    description: (
      <>
        This example showcases basic ATR-based trailing stop loss functionality. The stop loss continuously adjusts based on Average True Range
        values, maintaining a dynamic distance from current price that adapts to market volatility. The trailing activation begins immediately at
        position opening, protecting the trade from the start.
        <br />
        <br />
        The forward adjustment of 3.50 multiplies the raw ATR value, creating sufficient breathing room for normal price fluctuations. As volatility
        increases, the stop loss automatically widens its distance; as markets calm, it tightens. This responsive behavior prevents both premature
        exits and excessive risk exposure.
        <br />
        <br />
        The low 1.10 risk ratio take profit creates a tight target suitable for volatile conditions. This combination of adaptive stops and modest
        targets works well in trending markets where prices move steadily but with varying volatility levels.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - ATR
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.10</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1460/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1621.txt",
    name: "Demonstration of Functionality | Stop Loss Trailing | Example C2: First breakeven, then average true range stop loss trailing",
    description: (
      <>
        This example demonstrates multi-stage stop loss trailing with breakeven and ATR components. The first stage moves the stop to breakeven when
        price reaches 120% of the initial risk, securing a risk-free trade. At 150%, the system switches to ATR-based trailing with a tighter 1.50
        multiplier for closer tracking.
        <br />
        <br />
        Two different ATR configurations provide flexibility - the initial stop uses a 2.50 multiplier for wider placement, while the trailing stage
        uses 1.50 for tighter following. This graduated approach balances initial trade development needs with later profit protection requirements.
        <br />
        <br />
        The stage-based system allows sophisticated risk management that adapts to trade progression. Early stages give trades room to develop, while
        later stages lock in profits more aggressively. This professional approach maximizes both win rate and profit potential.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - ATR (1)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
              <li>
                Forward adjustment = <b>2.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - ATR (2)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
              <li>
                Forward adjustment = <b>1.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR (1)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Breakeven
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Breakeven</b>
              </li>
              <li>
                Breakeven position = <b>120</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>150</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR (2)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1621/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8396.txt",
    name: "Demonstration of Functionality | Stop Loss Trailing | Example C3: First breakeven, then average true range, then market stop loss trailing",
    description: (
      <>
        This example extends trailing stops to three stages: breakeven, ATR-based, and market-based trailing. Each stage activates at specific profit
        levels, creating increasingly aggressive profit protection as trades succeed. The final market-based stage follows price extremely closely
        using previous candle extremes.
        <br />
        <br />
        The market trailing stage adds both fixed and spread-based buffers, preventing stops from triggering on normal spread widening. With no take
        profit set, trades can run indefinitely until the trailing stop captures profits. This open-ended approach captures major trends while
        protecting accumulated gains.
        <br />
        <br />
        Progressive staging represents advanced position management, allowing different strategies at different profit levels. This flexibility helps
        traders adapt to changing market conditions within a single trade, maximizing profit potential while managing risk dynamically.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - ATR (1)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
              <li>
                Forward adjustment = <b>2.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - ATR (2)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
              <li>
                Forward adjustment = <b>1.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR (1)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Breakeven
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Breakeven</b>
              </li>
              <li>
                Breakeven position = <b>120</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>150</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR (2)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Market
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>200</b>
              </li>
              <li>
                Type = <b>Market</b>
              </li>
              <li>
                Market buffer fix = <b>10</b>
              </li>
              <li>
                Market buffer spread = <b>4.0</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8396/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Initial Stop Loss and Initial Take Profit
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module4670.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D1: Fixed pips distance as initial stop loss and initial take profit",
    description: (
      <>
        This example uses fixed pip values for both stop loss and take profit levels. The 200 pip stop loss and 300 pip take profit create a favorable
        1.5 risk-reward ratio using absolute distances. This straightforward approach requires no complex calculations, making it ideal for beginners
        learning position management basics.
        <br />
        <br />
        Fixed pip strategies work best in stable volatility environments where typical price movements remain consistent. The recommendation for
        timeframes between M1 and H1 suggests this approach suits intraday trading where daily ranges are relatively predictable.
        <br />
        <br />
        While simple, fixed pip methods may require adjustment as market conditions change over time. Traders should periodically review these values
        to ensure they remain appropriate for current market volatility levels. This example provides an excellent starting point for understanding
        risk-reward concepts.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M1 - H1
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Fix pips</b>
              </li>
              <li>
                Fix pips = <b>200</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Fix pips</b>
              </li>
              <li>
                Fix pips = <b>300</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module4670/example_image_1.png",
        description: "Expected behavior:",
        height: "536px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8563.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D2: Indicator value for initial stop loss and initial take profit",
    description: (
      <>
        This example uses Bollinger Bands to dynamically set both stop loss and take profit levels. The lower band determines stop loss placement
        while the upper band sets take profit targets, creating a statistically-based range for each trade. This adaptive approach automatically
        adjusts to market volatility without manual intervention.
        <br />
        <br />
        Using indicators for both levels ensures consistent risk-reward relationships regardless of market conditions. As Bollinger Bands expand
        during volatile periods, both stops and targets widen proportionally. During calm periods, they contract together, maintaining strategic
        balance.
        <br />
        <br />
        The MACD provides entry timing while Bollinger Bands handle exit levels entirely. This separation of concerns creates a robust system where
        entry and exit logic operate independently, allowing optimization of each component without affecting the other.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Lower)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Lower</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Upper)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Upper</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>4</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Lower)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Upper)</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8563/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8362.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D3: Indicator value for initial stop loss, stop loss trailing, initial take profit and dynamic take profit",
    description: (
      <>
        This example combines indicator-based levels with dynamic adjustments for both stop loss and take profit. Bollinger Bands provide initial
        levels and continue adjusting them throughout the trade duration. This creates a fully dynamic system where both risk and reward targets
        respond to changing market conditions.
        <br />
        <br />
        The trailing stop and dynamic take profit activate immediately, continuously repositioning based on current Bollinger Band values. As bands
        expand or contract, both levels adjust accordingly. This creates a breathing trade management system that adapts to volatility in real-time.
        <br />
        <br />
        This advanced approach suits experienced traders comfortable with fully automated position management. The system requires no manual
        intervention once trades open, making it ideal for algorithmic trading where consistent rule application matters more than discretionary
        judgment.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Lower)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Lower</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Upper)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Upper</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>4</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Lower)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Lower)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Upper)</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Dynamic Take Profit - Indicator value
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB (Upper)</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8362/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module4406.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D4: Support and resistance for initial stop loss and initial take profit",
    description: (
      <>
        This example uses chart analyzer support and resistance levels for stop loss and take profit placement. The analyzer identifies key price
        levels automatically, placing stops below support for long trades and targets at resistance levels. Minimum distance requirements ensure
        levels aren't too close to entry prices.
        <br />
        <br />
        The 0.00250000 minimum rate of change for stop loss and 0.00300000 for take profit create appropriate spacing from entry prices. These
        minimums prevent the system from using support or resistance levels that are too close, which could result in premature exits or unrealistic
        targets.
        <br />
        <br />
        Chart-based levels often align with psychological price points where many traders place orders. This creates self-fulfilling prophecies as
        prices react at these levels. Using them for trade management aligns strategies with broader market participant behavior.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Draw boundaries = <b>Off</b>
              </li>
              <li>
                Draw extrema = <b>Off</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>Off</b>
              </li>
              <li>
                Supports and resistances forward = <b>ALL</b>
              </li>
              <li>
                Support multiplier = <b>1.00</b>
              </li>
              <li>
                Resistance multiplier = <b>1.00</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00250000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00300000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module4406/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2412.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D5: Support and resistance for initial stop loss, stop loss trailing, initial take profit and dynamic take profit",
    description: (
      <>
        This example extends support and resistance usage to include dynamic adjustments throughout trade duration. Both stop loss and take profit
        levels continuously update as new support and resistance levels form. This creates an intelligent system that adapts to evolving market
        structure.
        <br />
        <br />
        The chart analyzer continuously scans for new levels, switching to closer support or resistance as they develop. This allows trades to tighten
        stops as price moves favorably while extending targets when resistance breaks. The visual display shows all detected levels, providing
        transparency in decision-making.
        <br />
        <br />
        This sophisticated approach mimics how professional traders manually adjust levels based on market structure. Automation ensures consistent
        application without emotional interference, maintaining disciplined trade management even during stressful market conditions.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Draw boundaries = <b>Off</b>
              </li>
              <li>
                Draw extrema = <b>Off</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>Off</b>
              </li>
              <li>
                Supports and resistances forward = <b>ALL</b>
              </li>
              <li>
                Support multiplier = <b>1.00</b>
              </li>
              <li>
                Resistance multiplier = <b>1.00</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00250000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Stop Loss Trailing - Support/resistance from chart analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Support/resistance fix pips minimum = <b>Off</b>
              </li>
              <li>
                Support/resistance rate of change minimum = <b>Off</b>
              </li>
              <li>
                Support/resistance fix pips maximum = <b>Off</b>
              </li>
              <li>
                Support/resistance rate of change maximum = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00300000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Dynamic Take Profit - Support/resistance from chart analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Activation position = <b>0</b>
              </li>
              <li>
                Type = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Support/resistance fix pips minimum = <b>Off</b>
              </li>
              <li>
                Support/resistance rate of change minimum = <b>Off</b>
              </li>
              <li>
                Support/resistance fix pips maximum = <b>Off</b>
              </li>
              <li>
                Support/resistance rate of change maximum = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2412/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9923.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D6: Candle pattern search used for initial stop loss and initial take profit",
    description: (
      <>
        This example uses candle pattern recognition to determine optimal stop loss and take profit levels. When patterns like hammers or engulfing
        formations occur, they provide ideal exit levels based on the pattern's structure. The 2.00 forward adjustment allows fine-tuning of these
        idealized levels.
        <br />
        <br />
        Multiple patterns are enabled, increasing signal frequency and providing diverse entry opportunities. Each pattern has specific
        characteristics that suggest probable price behavior, making them valuable for exit level determination. The visual pattern identification
        helps traders learn pattern recognition while the system trades automatically.
        <br />
        <br />
        Pattern-based exits align with traditional technical analysis principles, using the same concepts manual traders have employed for decades.
        This bridges classical charting techniques with modern algorithmic trading.
        <br />
        <br />
        <b>Please note:</b> Recommended to use M1 - M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Candle Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Hammer = <b>On</b>
              </li>
              <li>
                Hanging Man = <b>On</b>
              </li>
              <li>
                Bullish Belt Hold = <b>On</b>
              </li>
              <li>
                Bearish Belt Hold = <b>On</b>
              </li>
              <li>
                Bullish Engulfing = <b>On</b>
              </li>
              <li>
                Bearish Engulfing = <b>On</b>
              </li>
              <li>
                Bullish Harami = <b>On</b>
              </li>
              <li>
                Bearish Harami = <b>On</b>
              </li>
              <li>
                Bullish Harami Cross = <b>On</b>
              </li>
              <li>
                Bearish Harami Cross = <b>On</b>
              </li>
              <li>
                Bullish Doji = <b>On</b>
              </li>
              <li>
                Bearish Doji = <b>On</b>
              </li>
              <li>
                Bullish Long Legged Doji = <b>On</b>
              </li>
              <li>
                Bearish Long Legged Doji = <b>On</b>
              </li>
              <li>
                Bullish Gravestone Doji = <b>On</b>
              </li>
              <li>
                Bearish Gravestone Doji = <b>On</b>
              </li>
              <li>
                Bullish Dragonfly Doji = <b>On</b>
              </li>
              <li>
                Bearish Dragonfly Doji = <b>On</b>
              </li>
              <li>
                Dark Cloud Cover = <b>On</b>
              </li>
              <li>
                Piercing Pattern = <b>On</b>
              </li>
              <li>
                Morning Star = <b>On</b>
              </li>
              <li>
                Evening Star = <b>On</b>
              </li>
              <li>
                Bullish Inside Days = <b>On</b>
              </li>
              <li>
                Bearish Inside Days = <b>On</b>
              </li>
              <li>
                Stop loss forward adjustment = <b>2.00</b>
              </li>
              <li>
                Take profit forward adjustment = <b>2.00</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Candle patterns</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Candle patterns</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9923/example_image_1.png",
        description: "Expected behavior:",
        height: "543px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3274.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D7: Chart pattern search used for initial stop loss and initial take profit",
    description: (
      <>
        This example employs chart pattern detection for trade management. Patterns like triangles, head-and-shoulders, and double tops provide
        structured exit levels based on measured moves. The 1.00 forward adjustment means patterns' ideal levels are used without modification,
        trusting classical technical analysis principles.
        <br />
        <br />
        Eighteen different patterns are enabled, covering continuation and reversal formations. Each pattern has well-established profit targets based
        on pattern height or width, providing objective exit levels. The system recognizes these patterns automatically, eliminating subjective
        interpretation.
        <br />
        <br />
        Chart patterns represent crowd psychology visualized in price action. Using them for trade management aligns strategies with these
        psychological levels where significant trading activity often occurs, increasing the probability of successful exits.
        <br />
        <br />
        <b>Please note:</b> Recommended to use M1 - M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Bullish Wedge = <b>On</b>
              </li>
              <li>
                Bearish Wedge = <b>On</b>
              </li>
              <li>
                Bullish Trendchannel = <b>On</b>
              </li>
              <li>
                Bearish Trendchannel = <b>On</b>
              </li>
              <li>
                Bullish Triangle = <b>On</b>
              </li>
              <li>
                Bearish Triangle = <b>On</b>
              </li>
              <li>
                Shoulder Head Shoulder Top = <b>On</b>
              </li>
              <li>
                Shoulder Head Shoulder Bottom = <b>On</b>
              </li>
              <li>
                Double Top = <b>On</b>
              </li>
              <li>
                Double Bottom = <b>On</b>
              </li>
              <li>
                Triple Top = <b>On</b>
              </li>
              <li>
                Triple Bottom = <b>On</b>
              </li>
              <li>
                Rectangle Top = <b>On</b>
              </li>
              <li>
                Rectangle Bottom = <b>On</b>
              </li>
              <li>
                Bullish Symetric Triangle = <b>On</b>
              </li>
              <li>
                Bearish Symetric Triangle = <b>On</b>
              </li>
              <li>
                Bullish Gap = <b>On</b>
              </li>
              <li>
                Bearish Gap = <b>On</b>
              </li>
              <li>
                Stop loss forward adjustment = <b>1.00</b>
              </li>
              <li>
                Take profit forward adjustment = <b>1.00</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Chart patterns</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Chart patterns</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3274/example_image_1.png",
        description: "Expected behavior:",
        height: "596px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3122.txt",
    name: "Demonstration of Functionality | Initial Stop Loss and Initial Take Profit | Example D8: Fibonacci pattern search used for initial stop loss and initial take profit",
    description: (
      <>
        This example utilizes Fibonacci retracement patterns for trade management. When bullish or bearish Fibonacci patterns form, they provide
        natural stop loss and take profit levels based on key retracement percentages. These mathematical relationships often create strong support
        and resistance zones.
        <br />
        <br />
        The 1.00 forward adjustment indicates Fibonacci levels are used exactly as calculated, respecting the mathematical precision these ratios
        represent. Traders have observed these levels for decades, creating self-reinforcing price behavior at key Fibonacci points.
        <br />
        <br />
        Fibonacci-based management suits trend-following strategies where retracements offer entry opportunities. The pattern recognition eliminates
        manual Fibonacci drawing, ensuring consistent application across all trades while maintaining the mathematical integrity of these classical
        levels.
        <br />
        <br />
        <b>Please note:</b> Recommended to use M1 - M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Fibonacci Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Bullish Fibonacci = <b>On</b>
              </li>
              <li>
                Bearish Fibonacci = <b>On</b>
              </li>
              <li>
                Stop loss forward adjustment = <b>1.00</b>
              </li>
              <li>
                Take profit forward adjustment = <b>1.00</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Fibonacci patterns</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Fibonacci patterns</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3122/example_image_1.png",
        description: "Expected behavior:",
        height: "429px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Trade Splitting
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3521.txt",
    name: "Demonstration of Functionality | Trade Splitting | Example E1: Two trades with a pending order",
    description: (
      <>
        This example demonstrates trade splitting with pending orders. The system opens two positions from each signal - 100% volume immediately and
        another 50% at 80 % risk. This graduated entry approach allows adding to winning positions while limiting exposure on losing trades.
        <br />
        <br />
        The pending order uses stop order mechanics with Standard Deviation-based pricing. If price moves to 80 % risk, the additional position
        triggers automatically. Both positions share the same stop loss and take profit logic, simplifying management despite multiple entries.
        <br />
        <br />
        Position scaling represents professional money management, allowing traders to maximize profitable opportunities while controlling risk. This
        approach captures more profit from strong moves while maintaining reasonable exposure on failed signals.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>100</b>
              </li>
              <li>
                Open position = <b>80</b>
              </li>
              <li>
                Close position = <b>0</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3521/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module4421.txt",
    name: "Demonstration of Functionality | Trade Splitting | Example E2: Three trades",
    description: (
      <>
        This example extends trade splitting to three positions with different entry and exit points. The first split enters at 80 % risk with full
        volume, while the second enters at 150 % risk with 50% volume and exits at 200 % risk. This creates a sophisticated scaling system with
        multiple profit targets.
        <br />
        <br />
        Each split can have independent exit levels, allowing partial profit-taking at predetermined points. This approach locks in gains
        progressively while keeping portions of the position for larger moves. The main trade targets 2.40 risk ratio, seeking substantial profits on
        the core position.
        <br />
        <br />
        Multiple exit points reduce psychological pressure by securing profits incrementally. Traders often struggle with all-or-nothing exits; this
        graduated approach provides a balanced solution that satisfies both conservative and aggressive instincts.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>2.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting 1
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>100</b>
              </li>
              <li>
                Open position = <b>80</b>
              </li>
              <li>
                Close position = <b>0</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting 2
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>50</b>
              </li>
              <li>
                Open position = <b>150</b>
              </li>
              <li>
                Close position = <b>200</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module4421/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9338.txt",
    name: "Demonstration of Functionality | Trade Splitting | Example E3: Four trades",
    description: (
      <>
        This example implements four-position trade splitting, creating a complex scaling strategy. Each split has specific entry points and volumes,
        with the final position using only 25% volume entering at 200 % risk. This pyramid approach maximizes position size in winning trades while
        minimizing risk.
        <br />
        <br />
        The staggered entries at 80, 150, and 200 % risk create multiple confirmation points before full position deployment. Early exits at 200 and
        220 % risk for later splits ensure profit-taking even if trends reverse. This sophisticated structure balances aggression with protection.
        <br />
        <br />
        Four-level splitting represents advanced position management suitable for strong trending markets. The complexity requires careful monitoring
        but can significantly enhance profitability when trends persist beyond initial targets.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>2.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting 1
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>100</b>
              </li>
              <li>
                Open position = <b>80</b>
              </li>
              <li>
                Close position = <b>0</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting 2
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>50</b>
              </li>
              <li>
                Open position = <b>150</b>
              </li>
              <li>
                Close position = <b>200</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Trade Splitting 3
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Open volume = <b>25</b>
              </li>
              <li>
                Open position = <b>200</b>
              </li>
              <li>
                Close position = <b>220</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9338/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Performance and drawdown monitoring
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module5238.txt",
    name: "Demonstration of Functionality | Performance and drawdown monitoring | Example F1: Temporary disabling of trading due to bad performance",
    description: (
      <>
        This example shows temporary trading suspension due to poor weekly performance. If the account drops 2% or more in a week, trading halts until
        the next week begins. The inspector panel displays current performance metrics, providing real-time feedback on proximity to suspension
        thresholds.
        <br />
        <br />
        This protective mechanism prevents emotional revenge trading after losses. By forcing a cooling-off period, traders can analyze what went
        wrong without risking further capital. The automatic reset each week provides fresh starts without manual intervention.
        <br />
        <br />
        Performance monitoring creates self-aware trading systems that recognize when strategies aren't working. This defensive feature proves
        invaluable during unexpected market conditions when strategies may temporarily fail.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Monitoring
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw inspector panel = <b>On</b>
              </li>
              <li>
                Weekly performance: Relative lower limit = <b>-2.00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module5238/example_image_1.png",
        description: "Expected behavior:",
        height: "605px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module5255.txt",
    name: "Demonstration of Functionality | Performance and drawdown monitoring | Example F2: Permanent disabling of trading due to bad performance",
    description: (
      <>
        This example implements permanent trading suspension based on overall performance. If total account performance drops below the -2% threshold,
        trading stops completely without automatic reactivation. This ultimate protection prevents complete account destruction during strategy
        failure.
        <br />
        <br />
        The inspector panel continuously displays overall performance metrics, warning traders as they approach the permanent suspension threshold.
        This transparency allows intervention before automatic shutdown occurs. Manual reset remains possible but requires deliberate action.
        <br />
        <br />
        Permanent suspension represents the final safety net, acknowledging that strategies can fail completely. This humble approach accepts that not
        all strategies work forever, protecting capital when fundamental strategy assumptions break down.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Monitoring
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw inspector panel = <b>On</b>
              </li>
              <li>
                Overall performance: Relative lower limit = <b>2.00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module5255/example_image_1.png",
        description: "Expected behavior:",
        height: "605px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module6743.txt",
    name: "Demonstration of Functionality | Performance and drawdown monitoring | Example F3: Temporary disabling of trading due to bad drawdown",
    description: (
      <>
        This example uses weekly drawdown limits for temporary suspension. If drawdown exceeds 2% within a week, trading pauses until the next week.
        Unlike performance limits that measure end results, drawdown limits monitor peak-to-trough movements during the period.
        <br />
        <br />
        Drawdown monitoring captures risk exposure more accurately than simple performance metrics. A strategy might end profitably while experiencing
        dangerous drawdowns that risk large losses. This measurement ensures strategies maintain acceptable risk levels throughout trading periods.
        <br />
        <br />
        The inspector panel shows current drawdown levels, helping traders understand their risk exposure in real-time. This awareness promotes better
        risk management decisions even when trading manually alongside automated systems.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Monitoring
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw inspector panel = <b>On</b>
              </li>
              <li>
                Weekly drawdown : Relative upper limit = <b>2.00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module6743/example_image_1.png",
        description: "Expected behavior:",
        height: "605px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9352.txt",
    name: "Demonstration of Functionality | Performance and drawdown monitoring | Example F4: Permanent disabling of trading due to bad drawdown",
    description: (
      <>
        This example enforces permanent suspension based on maximum overall drawdown. If drawdown ever exceeds 2% from the highest account equity,
        trading halts permanently. This stringent protection ensures strategies never risk unacceptable losses regardless of eventual recovery.
        <br />
        <br />
        Overall drawdown represents the worst-case scenario experienced since strategy inception. This metric reveals true risk exposure, showing the
        maximum loss traders must psychologically and financially withstand. Limiting this preserves both capital and confidence.
        <br />
        <br />
        The permanent nature of this suspension reflects drawdown's serious implications. Strategies exceeding acceptable drawdown levels have proven
        themselves too risky, warranting complete reevaluation rather than simple continuation.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Fix lots</b>
              </li>
              <li>
                Fix lots = <b>0.10</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Monitoring
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw inspector panel = <b>On</b>
              </li>
              <li>
                Overall drawdown: Relative upper limit = <b>2.00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9352/example_image_1.png",
        description: "Expected behavior:",
        height: "605px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Pending orders
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9763.txt",
    name: "Demonstration of Functionality | Pending orders | Example G1: Buy stop and StdDev pending price",
    description: (
      <>
        This example demonstrates buy stop pending orders with Standard Deviation-based pricing. Orders are placed above current market price using
        the STD indicator value, triggering only if price rises to that level. The 75-bar expiration ensures orders don't remain indefinitely if
        unused.
        <br />
        <br />
        Stop orders excel in breakout strategies where traders want confirmation of upward movement before entering. By waiting for price to reach the
        pending level, the strategy avoids false breakouts that immediately reverse. The STD-based pricing adapts to volatility, placing orders at
        statistically significant levels.
        <br />
        <br />
        Pending orders reduce slippage during fast markets by pre-positioning orders at desired levels. This proves especially valuable during news
        events or breakouts when immediate execution might suffer from poor fills.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9763/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2003.txt",
    name: "Demonstration of Functionality | Pending orders | Example G2: Buy limit and StdDev pending price",
    description: (
      <>
        This example shows buy limit orders for entering at better prices during retracements. Orders are placed below market price using STD
        indicator values, waiting for pullbacks before entering long positions. This patient approach often achieves better entry prices than
        immediate market orders.
        <br />
        <br />
        Limit orders suit range-trading strategies where price oscillates between boundaries. By placing orders at the lower boundary, traders enter
        at optimal levels within the range. The STD-based pricing ensures orders adapt to current volatility conditions.
        <br />
        <br />
        The 75-bar expiration prevents old orders from triggering after market conditions change. This temporal limit maintains strategy relevance,
        ensuring orders reflect recent market analysis rather than outdated signals.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Limit</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2003/example_image_1.png",
        description: "Expected behavior:",
        height: "577px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module6172.txt",
    name: "Demonstration of Functionality | Pending orders | Example G3: Sell stop and StdDev pending price",
    description: (
      <>
        This example implements sell stop orders for short positions, placing orders below current price to confirm downward momentum. The STD
        indicator determines optimal placement distance, adapting to market volatility. This ensures orders trigger only on meaningful downward moves.
        <br />
        <br />
        Sell stop orders protect against false breakdowns in uptrends by requiring price confirmation before entering short positions. This
        conservative approach reduces premature shorts that suffer immediate losses when uptrends resume. The pending mechanism provides this
        confirmation filter.
        <br />
        <br />
        The visual display shows pending order levels clearly, helping traders understand where orders wait for execution. This transparency aids in
        learning how pending orders interact with market price action.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Short</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module6172/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9758.txt",
    name: "Demonstration of Functionality | Pending orders | Example G4: Sell limit and StdDev pending price",
    description: (
      <>
        This example uses sell limit orders to enter short positions at better prices during rallies. Orders are placed above current price, waiting
        for upward retracements before selling. This patient approach often achieves superior entry prices for short trades.
        <br />
        <br />
        Limit orders for shorts work well in downtrends where rallies provide better selling opportunities. By waiting for these bounces, traders
        enter at resistance levels rather than chasing price lower. The STD-based pricing ensures appropriate distance from current market levels.
        <br />
        <br />
        The combination of MACD signals and limit order execution creates a two-step confirmation process. First, the MACD must signal, then price
        must reach the limit level, providing double verification before position entry.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Short</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Limit</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9758/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3851.txt",
    name: "Demonstration of Functionality | Pending orders | Example G5: Buy stop and rate of change pending price",
    description: (
      <>
        This example employs rate of change calculations for pending order placement. The 0.00300000 rate of change creates percentage-based pending
        prices that scale with asset value. This relative approach maintains consistent strategy behavior as prices fluctuate over time.
        <br />
        <br />
        Fixed 200 pip stops might be appropriate at certain price levels but become too large or small as markets evolve. Rate of change calculations
        solve this by maintaining proportional relationships regardless of absolute price levels. This creates more robust long-term strategies.
        <br />
        <br />
        The M1-M15 timeframe recommendation suggests this approach suits faster trading where quick price movements require responsive pending orders.
        The percentage-based system adapts automatically to different volatility regimes.
        <br />
        <br />
        <b>Please note:</b> Recommended to use M1 - M15
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Rate of change</b>
              </li>
              <li>
                Rate of change = <b>0.00300000</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Rate of change</b>
              </li>
              <li>
                Rate change = <b>0.00250000</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3851/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2994.txt",
    name: "Demonstration of Functionality | Pending orders | Example G6: Buy stop and indicator value for pending price",
    description: (
      <>
        This example uses Bollinger Band upper values for stop order placement. When MACD signals occur, pending orders are placed at the upper band
        level, requiring price to break above this statistical boundary before entering. This combines momentum confirmation with volatility-based
        levels.
        <br />
        <br />
        Bollinger Bands provide dynamic resistance levels that expand and contract with volatility. Using these for pending orders ensures entries
        only occur when price breaks significant levels, not just minor resistance. This filtering reduces false breakouts in ranging markets.
        <br />
        <br />
        The indicator-based pending price eliminates subjective level selection, creating consistent, mathematically-derived entry points. This
        objectivity removes emotional decision-making from trade entry timing.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>BB</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (BB Upper)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>BB</b>
              </li>
              <li>
                Line index = <b>Upper</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2994/example_image_1.png",
        description: "Expected behavior:",
        height: "551px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9677.txt",
    name: "Demonstration of Functionality | Pending orders | Example G7: Buy stop and support and resistance for pending price",
    description: (
      <>
        This example utilizes chart analyzer support and resistance for pending order placement. The analyzer identifies key levels automatically,
        placing pending orders at these psychological price points. This aligns entries with levels where significant trading activity typically
        occurs.
        <br />
        <br />
        Support and resistance levels often act as magnets for price, attracting order flow from many market participants. Placing pending orders at
        these levels increases fill probability while ensuring entry at significant price points. The 75-bar expiration prevents outdated levels from
        triggering trades.
        <br />
        <br />
        Visual display of support and resistance helps traders understand the chart analyzer's decision process. This transparency builds confidence
        in automated level selection while educating traders about market structure.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Draw boundaries = <b>Off</b>
              </li>
              <li>
                Draw extrema = <b>Off</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>Off</b>
              </li>
              <li>
                Supports and resistances forward = <b>ALL</b>
              </li>
              <li>
                Support multiplier = <b>1.00</b>
              </li>
              <li>
                Resistance multiplier = <b>1.00</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9677/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9667.txt",
    name: "Demonstration of Functionality | Pending orders | Example G8: Buy stop and price dependent activation (hysteresis min)",
    description: (
      <>
        This example introduces hysteresis for locally managed pending orders. The -50 % minimum hysteresis requires price to move beyond the pending
        level before triggering, preventing activation from brief touches. This filters out false breakouts that immediately reverse.
        <br />
        <br />
        Local management keeps pending orders invisible to brokers, preventing stop hunting or front-running. The hysteresis feature adds another
        protection layer, ensuring meaningful price movement before activation. This double protection significantly reduces false entries.
        <br />
        <br />
        The ATR-based pending price combines with hysteresis to create adaptive entry conditions. As volatility changes, both the pending level and
        hysteresis requirement adjust proportionally, maintaining consistent strategy behavior.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
              <li>
                Locally managed limit and stop orders = <b>On</b>
              </li>
              <li>
                Hysteresis minimum value = <b>-50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (ATR)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9667/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9222.txt",
    name: "Demonstration of Functionality | Pending orders | Example G9: Buy stop and price dependent cancellation (hysteresis max)",
    description: (
      <>
        This example demonstrates maximum hysteresis for automatic order cancellation. If price moves 50 pips beyond the pending level without
        triggering, the order cancels automatically. This prevents late entries after the opportunity has passed, maintaining strategy timing
        integrity.
        <br />
        <br />
        Maximum hysteresis acts like a stop-limit order, defining both trigger and maximum acceptable entry prices. This prevents slippage during fast
        markets while ensuring entries occur within acceptable ranges. The automatic cancellation removes outdated orders that no longer reflect
        current conditions.
        <br />
        <br />
        Combined with local management, this creates sophisticated pending order behavior impossible with standard broker orders. The flexibility
        enables precise entry control tailored to specific strategy requirements.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Pending Order Settings
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Mode = <b>Stop</b>
              </li>
              <li>
                Price calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>ATR</b>
              </li>
              <li>
                Expiration = <b>75</b>
              </li>
              <li>
                Locally managed limit and stop orders = <b>On</b>
              </li>
              <li>
                Hysteresis maximum value = <b>-50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (ATR)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>ATR</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9222/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | News trading
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module6638.txt",
    name: "Demonstration of Functionality | News trading | Example H1: Events filtered by title",
    description: (
      <>
        This example filters news events by title content, focusing only on ECB and Fed announcements. The exclusive filter ensures only events
        containing these specific terms generate signals. This targeted approach eliminates noise from less impactful news while focusing on major
        central bank decisions.
        <br />
        <br />
        The visual event table and chart lines display filtered events clearly, showing exactly which announcements will trigger signals. The
        30-minute pre and post windows provide adequate time for positioning around these major events. Title filtering allows precise event selection
        without monitoring all news.
        <br />
        <br />
        Central bank events often create the largest market movements, making them ideal for news trading strategies. By filtering specifically for
        these events, traders can prepare thoroughly for each opportunity rather than reacting to numerous minor announcements.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Draw table with upcoming events = <b>On</b>
              </li>
              <li>
                Draw signal lines with name in chart = <b>On</b>
              </li>
              <li>
                Pre event signal time = <b>1800</b>
              </li>
              <li>
                Post event signal time = <b>1800</b>
              </li>
              <li>
                Signals for ALL regions = <b>On</b>
              </li>
              <li>
                Signals for AUD region = <b>On</b>
              </li>
              <li>
                Signals for BRL region = <b>On</b>
              </li>
              <li>
                Signals for CAD region = <b>On</b>
              </li>
              <li>
                Signals for CHF region = <b>On</b>
              </li>
              <li>
                Signals for CNY region = <b>On</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>On</b>
              </li>
              <li>
                Signals for HKD region = <b>On</b>
              </li>
              <li>
                Signals for INR region = <b>On</b>
              </li>
              <li>
                Signals for JPY region = <b>On</b>
              </li>
              <li>
                Signals for KRW region = <b>On</b>
              </li>
              <li>
                Signals for MXN region = <b>On</b>
              </li>
              <li>
                Signals for NOK region = <b>On</b>
              </li>
              <li>
                Signals for NZD region = <b>On</b>
              </li>
              <li>
                Signals for SEK region = <b>On</b>
              </li>
              <li>
                Signals for SGD region = <b>On</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>On</b>
              </li>
              <li>
                Title filter 1
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Filter type = <b>Exclusive</b>
                  </li>
                  <li>
                    Title name to filter = <b>ECB</b>
                  </li>
                </ul>
              </li>
              <li>
                Title filter 2
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Filter type = <b>Exclusive</b>
                  </li>
                  <li>
                    Title name to filter = <b>Fed</b>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module6638/example_image_1.png",
        description: "Expected behavior:",
        height: "563px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module5742.txt",
    name: "Demonstration of Functionality | News trading | Example H2: Events filtered by relation",
    description: (
      <>
        This example filters events by actual versus previous value relationships. Only events where actual results exceed previous values generate
        signals, targeting positive surprises that often drive prices higher. This relationship filtering doesn't require knowing absolute values,
        only relative improvements.
        <br />
        <br />
        The visual display shows which events meet the relationship criteria, providing transparency in signal generation. Value relationships prove
        more reliable than absolute thresholds since economic indicators vary widely in scale and significance. This relative approach adapts to
        different types of announcements.
        <br />
        <br />
        Focusing on improvements filters out disappointing results that might trigger selling. This directional bias aligns signals with likely market
        reactions, improving signal quality without complex fundamental analysis.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - News
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Draw table with upcoming events = <b>On</b>
              </li>
              <li>
                Draw signal lines with name in chart = <b>On</b>
              </li>
              <li>
                Pre event signal time = <b>1800</b>
              </li>
              <li>
                Post event signal time = <b>1800</b>
              </li>
              <li>
                Signals for ALL regions = <b>On</b>
              </li>
              <li>
                Signals for AUD region = <b>On</b>
              </li>
              <li>
                Signals for BRL region = <b>On</b>
              </li>
              <li>
                Signals for CAD region = <b>On</b>
              </li>
              <li>
                Signals for CHF region = <b>On</b>
              </li>
              <li>
                Signals for CNY region = <b>On</b>
              </li>
              <li>
                Signals for EUR region = <b>On</b>
              </li>
              <li>
                Signals for GBP region = <b>On</b>
              </li>
              <li>
                Signals for HKD region = <b>On</b>
              </li>
              <li>
                Signals for INR region = <b>On</b>
              </li>
              <li>
                Signals for JPY region = <b>On</b>
              </li>
              <li>
                Signals for KRW region = <b>On</b>
              </li>
              <li>
                Signals for MXN region = <b>On</b>
              </li>
              <li>
                Signals for NOK region = <b>On</b>
              </li>
              <li>
                Signals for NZD region = <b>On</b>
              </li>
              <li>
                Signals for SEK region = <b>On</b>
              </li>
              <li>
                Signals for SGD region = <b>On</b>
              </li>
              <li>
                Signals for USD region = <b>On</b>
              </li>
              <li>
                Signals for ZAR region = <b>On</b>
              </li>
              <li>
                Value relation 1
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Relation condition = <b>Actual greater previous</b>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module5742/example_image_1.png",
        description: "Expected behavior:",
        height: "457px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Indicators
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2915.txt",
    name: "Demonstration of Functionality | Indicators | Example I1: One indicator line used",
    description: (
      <>
        This example demonstrates basic indicator gradient conditions for signal generation. The SMA indicator generates signals when its slope
        crosses above zero, indicating the moving average has started rising. This simple condition identifies potential trend beginnings with minimal
        complexity.
        <br />
        <br />
        The gradient crossing approach differs from price-crossing methods by focusing on indicator direction changes rather than absolute positions.
        This often provides earlier signals as indicators begin turning before price crosses them. The visual signal lines clearly mark when gradient
        conditions are met.
        <br />
        <br />
        Single indicator strategies prove valuable for understanding core concepts before adding complexity. This example teaches how indicator slopes
        generate signals, forming the foundation for more sophisticated multi-indicator approaches.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Gradient condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2915/example_image_1.png",
        description: "Expected behavior:",
        height: "536px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3421.txt",
    name: "Demonstration of Functionality | Indicators | Example I2: One indicator line with time exclusion used",
    description: (
      <>
        This example adds time exclusion to indicator signals, blocking signals during specific hours. The 22:00-09:00 exclusion removes overnight and
        early morning periods when spreads may be wider and liquidity lower. This temporal filtering improves signal quality by focusing on optimal
        trading hours.
        <br />
        <br />
        Time exclusion differs from the timing component by completely disabling signal processing during excluded periods. Even if perfect indicator conditions occur, no signals are generated. This hard filter ensures strategies only operate during intended market sessions.
        <br />
        <br />
        The combination of technical indicators and time filtering creates more robust strategies. By eliminating unfavorable periods, the remaining
        signals have higher success probability due to better market conditions.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Gradient condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
              <li>
                Exclusion time range = <b>22:00:00-09:00:00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3421/example_image_1.png",
        description: "Expected behavior:",
        height: "527px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3232.txt",
    name: "Demonstration of Functionality | Indicators | Example I3: Two indicator lines combined",
    description: (
      <>
        This example combines two moving averages for signal generation using their crossing points. When the faster SMA crosses above the slower EMA,
        buy signals occur. This classic technical analysis approach has been used by traders for decades due to its simplicity and effectiveness.
        <br />
        <br />
        Moving average crossovers smooth price action while identifying trend changes. The dual-line approach provides confirmation as both averages
        must align for signals. Visual display of both lines helps traders understand the relationship dynamics driving signal generation.
        <br />
        <br />
        This foundational strategy teaches indicator combination principles applicable to any indicator pair. Understanding how two indicators
        interact prepares traders for more complex multi-indicator systems.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3232/example_image_1.png",
        description: "Expected behavior:",
        height: "536px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9577.txt",
    name: "Demonstration of Functionality | Indicators | Example I4: Four indicator lines combined",
    description: (
      <>
        This example employs four different moving averages with various conditions to create a comprehensive signal filter. Each average must meet
        specific gradient requirements for positive signals. The SMA and EMA use crossing conditions while LWMA and SMMA require positive slopes.
        <br />
        <br />
        Multiple indicator confirmation dramatically reduces false signals by requiring agreement from different calculation methods. Each moving
        average type responds differently to price changes, so unanimous agreement indicates strong trend conditions. The 100-period SMMA provides
        long-term trend confirmation.
        <br />
        <br />
        This complex filtering system demonstrates how multiple indicators can work together without creating contradictory signals. The graduated
        approach from fast to slow averages ensures both short-term momentum and long-term trend alignment.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>LWMA</b>
              </li>
              <li>
                Gradient condition = <b>Greater Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMMA</b>
              </li>
              <li>
                Period = <b>100</b>
              </li>
              <li>
                Gradient condition = <b>Greater Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>4</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9577/example_image_1.png",
        description: "Expected behavior:",
        height: "536px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1529.txt",
    name: "Demonstration of Functionality | Indicators | Example I5: Using price as indicator",
    description: (
      <>
        This example uses price itself as an indicator, specifically the opening price of each bar. This direct approach eliminates indicator lag,
        providing immediate response to price changes. The PRICE indicator allows any price component (open, high, low, close) to generate signals.
        <br />
        <br />
        Using raw price data suits breakout strategies where specific price levels matter more than smoothed indicators. The visual display shows
        exactly which price points generate signals, providing complete transparency. This approach teaches the difference between price-based and
        indicator-based signal generation.
        <br />
        <br />
        Price-as-indicator strategies often combine with other filters to avoid excessive signals. While responsive, raw price can be noisy, making
        additional confirmation valuable for filtering false moves.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - PRICE
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>PRICE</b>
              </li>
              <li>
                Series = <b>Open</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1529/example_image_1.png",
        description: "Expected behavior:",
        height: "534px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Oscillators
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2134.txt",
    name: "Demonstration of Functionality | Oscillators | Example J1: One oscillator line used as signal",
    description: (
      <>
        This example generates signals from RSI oscillator values crossing the 70 level, identifying overbought conditions. Unlike trend indicators
        that follow price, oscillators measure momentum and extremes, providing different signal types. The visual display shows RSI values and
        crossing points clearly.
        <br />
        <br />
        Oscillator signals often contradict trend signals, identifying potential reversals rather than continuations. The 70 level represents
        statistical overbought conditions where upward momentum may exhaust. This contrarian approach suits range-trading strategies where extremes
        reverse.
        <br />
        <br />
        Single oscillator strategies teach momentum concepts essential for understanding market dynamics. RSI's bounded 0-100 range makes
        interpretation straightforward, ideal for learning oscillator-based trading.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Value condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Value limit 1 = <b>70.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2134/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9327.txt",
    name: "Demonstration of Functionality | Oscillators | Example J2: Two oscillator lines combined as signal",
    description: (
      <>
        This example combines MACD histogram crossings for signal generation. When the MACD main line crosses above the signal line, momentum shifts
        from negative to positive. This zero-line crossing approach identifies momentum changes that often precede price moves.
        <br />
        <br />
        MACD combines trend and momentum elements, making it versatile for various market conditions. The histogram visualization shows the difference
        between lines, making crossings easy to identify. Both lines are displayed for complete understanding of the indicator dynamics.
        <br />
        <br />
        Two-line oscillators provide more nuanced signals than single-line versions. The relationship between lines reveals momentum acceleration and
        deceleration, offering insights beyond simple overbought/oversold conditions.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9327/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2321.txt",
    name: "Demonstration of Functionality | Oscillators | Example J3: Using volume as oscillator",
    description: (
      <>
        This example uses volume indicators for signal generation, requiring tick data for accurate calculation. Volume provides unique market
        insights unavailable from price alone, revealing the conviction behind price movements. Higher volume suggests stronger commitment to new
        price levels.
        <br />
        <br />
        The every-tick requirement ensures accurate volume measurement, though this demands more processing power. Volume-based signals often confirm
        price-based signals, providing additional validation. The oscillator display shows volume patterns clearly for analysis.
        <br />
        <br />
        Volume strategies work best in liquid markets where volume data is reliable. This example introduces non-price data integration, expanding
        strategy possibilities beyond pure technical analysis.
        <br />
        <br />
        <b>Please note:</b> Every tick as model required
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - VOLUME
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>VOLUME</b>
              </li>
              <li>
                Series = <b>Close</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2321/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Trade Basics
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9543.txt",
    name: "Demonstration of Functionality | Trade Basics | Example K1: Multiple open trades allowed",
    description: (
      <>
        This example allows multiple simultaneous trades from the same module, enabling position accumulation during strong trends. With one-trade
        mode disabled, each signal opens a new position regardless of existing trades. This aggressive approach maximizes exposure during favorable
        conditions.
        <br />
        <br />
        Multiple positions amplify both profits and risks, requiring careful money management. The visual display shows all open positions clearly,
        helping traders monitor total exposure. Fixed lot sizing prevents exponential position growth that could become unmanageable.
        <br />
        <br />
        This configuration suits strong trending markets where multiple entries can capture extended moves. However, traders must understand the
        increased risk from concentrated positions and adjust position sizing accordingly.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9543/example_image_1.png",
        description: "Expected behavior:",
        height: "439px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1510.txt",
    name: "Demonstration of Functionality | Trade Basics | Example K2: Signals used for opening and closing trades",
    description: (
      <>
        This example uses MACD crossings for both entry and exit signals, creating a complete trading system. Opposite crossings close existing
        positions, ensuring trades exit when momentum reverses. This symmetrical approach maintains consistent logic for both trade directions.
        <br />
        <br />
        Signal-based exits differ from stop loss/take profit exits by responding to market conditions rather than fixed levels. This adaptive approach
        can capture larger moves in strong trends while exiting quickly when conditions change. The system remains fully automated without manual
        intervention.
        <br />
        <br />
        Complete signal systems teach full trade lifecycle management. Understanding both entry and exit signal logic creates more coherent strategies
        than mixing different methodologies.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Exit</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1510/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3795.txt",
    name: "Demonstration of Functionality | Trade Basics | Example K3: Signal OR combination",
    description: (
      <>
        This example demonstrates OR logic for signal combination, where any positive signal triggers trades. The two moving averages operate
        independently - either crossing generates valid signals. This inclusive approach increases signal frequency compared to AND logic requiring
        both.
        <br />
        <br />
        OR logic suits diverse market conditions by allowing different indicators to capture various opportunity types. Fast SMA crossings catch quick
        moves while slower EMA crossings identify larger trends. The visual signals show which indicator triggered each trade.
        <br />
        <br />
        Logical operators fundamentally change strategy behavior. Understanding OR versus AND logic helps traders design systems matching their
        trading philosophy and risk tolerance.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                Signal logic = <b>Logical OR</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Period = <b>50</b>
              </li>
              <li>
                Gradient condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Period = <b>100</b>
              </li>
              <li>
                Gradient condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3795/example_image_1.png",
        description: "Expected behavior:",
        height: "534px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1454.txt",
    name: "Demonstration of Functionality | Trade Basics | Example K4: Forced waiting time between trades",
    description: (
      <>
        This example enforces 50-bar waiting periods between trades, preventing rapid successive entries. After each trade closes, the system must
        wait 50 bars before accepting new signals. This cooling-off period prevents overtrading and allows market conditions to develop.
        <br />
        <br />
        Forced waiting reduces emotional trading by enforcing discipline mechanically. Even perfect signals are ignored during waiting periods,
        maintaining consistent trade spacing. This approach suits strategies requiring specific market development between opportunities.
        <br />
        <br />
        Time-based trade spacing differs from signal-based spacing by using absolute time rather than market conditions. This creates predictable
        trade frequency useful for position sizing and risk management calculations.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
              <li>
                One trade mode = <b>Off</b>
              </li>
              <li>
                Waiting time between trades = <b>50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1454/example_image_1.png",
        description: "Expected behavior:",
        height: "536px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Visualization of signals
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module8718.txt",
    name: "Demonstration of Functionality | Visualization of signals | Example L1: MACD Long signal only",
    description: (
      <>
        This example displays long MACD signals without executing trades, useful for strategy development and signal analysis. Green vertical lines
        mark each bullish MACD crossing, providing visual confirmation of signal logic. This observation mode allows strategy refinement before
        risking capital.
        <br />
        <br />
        Signal-only mode helps traders understand when and why signals occur without financial commitment. By observing signals across different
        market conditions, traders can assess strategy viability before live trading. The visual markers create clear historical records for analysis.
        <br />
        <br />
        This educational approach bridges manual and automated trading. Traders can observe automated signals while taking discretionary trades,
        combining systematic signal generation with human judgment.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module8718/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module6800.txt",
    name: "Demonstration of Functionality | Visualization of signals | Example L2: MACD Short signal only",
    description: (
      <>
        This example visualizes short MACD signals using red vertical lines without executing trades. The bearish crossings are clearly marked,
        showing when downward momentum begins. This visualization helps traders understand short signal frequency and timing.
        <br />
        <br />
        Short signal visualization often reveals different patterns than long signals in the same market. Many assets have directional biases
        affecting signal distribution. Observing both directions helps traders choose optimal trading directions for specific instruments.
        <br />
        <br />
        Red signal lines create strong visual contrast, making bearish signals immediately apparent. This clear visualization aids in pattern
        recognition and strategy evaluation during backtesting or forward testing periods.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Short signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 2 cross value 1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module6800/example_image_1.png",
        description: "Expected behavior:",
        height: "629px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Volume Calculation
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2377.txt",
    name: "Demonstration of Functionality | Volume Calculation | Example M1: Indicator value for volume calculation with external indicator",
    description: (
      <>
        This example integrates external volume calculations through custom indicators, demonstrating advanced integration capabilities. The external
        MyExternalVolume indicator provides volume calculations beyond standard options. This flexibility allows proprietary algorithms while using
        external risk management.
        <br />
        <br />
        Custom indicators must be properly installed in MetaTrader's indicator folder with correct paths specified. The system forwards calculated
        values for position sizing, creating dynamic volume management based on custom logic. This bridges proprietary methods with systematic
        execution.
        <br />
        <br />
        External integration enables unlimited strategy sophistication. Traders can implement complex mathematical models, machine learning outputs,
        or proprietary calculations while leveraging the framework's trade management capabilities.
        <br />
        <br />
        <b>Please note:</b> To run this example, you'll need an external indicator. Just download, compile, and use the files listed below.
        <br />
        <br />
        <Link href={"/files/misc/MyExternalVolume.mq4"} download="MyExternalVolume.mq4" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalVolume.mq4`}
        </Link>
        <br />
        <Link href={"/files/misc/MyExternalVolume.mq5"} download="MyExternalVolume.mq5" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalVolume.mq5`}
        </Link>
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - CUS - Custom oscillator
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>CUS - Custom oscillator</b>
              </li>
              <li>
                MetaTrader4 path to indicator file = <b>\FEA Trading\MT4\MyExternalVolume.ex4</b>
              </li>
              <li>
                MetaTrader5 path to indicator file = <b>FEA Trading\MT5\MyExternalVolume</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>3</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value 1 cross value 2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Volume Calculation
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Volume calculation method = <b>Indicator value</b>
              </li>
              <li>
                Used indicator = <b>CUS</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2377/example_image_1.png",
        description: "Expected behavior:",
        height: "867px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Search Engines
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module4950.txt",
    name: "Demonstration of Functionality | Search Engines | Example N1: Non-traded signals generated by candle search",
    description: (
      <>
        This example demonstrates how the candle pattern search engine works without executing actual trades. The module is configured to detect
        various candlestick patterns and display them as signals on your chart. It monitors 24 different candle formations including Hammer, Doji
        variations, Engulfing patterns, and Morning/Evening Stars. When any of these patterns appear, the module generates a visual signal but doesn't
        open positions.
        <br />
        <br />
        The Data Feedback component is set to analyze detected patterns over 10,000 periods, creating valuable statistics about pattern frequency and
        distribution. This information helps traders understand which patterns occur most often in their chosen markets. The histogram feature with
        100 bins provides detailed analysis of pattern occurrences.
        <br />
        <br />
        This configuration is perfect for traders who want to study candlestick patterns before implementing them in live trading strategies. By
        observing how patterns form and their frequency, you can make informed decisions about which patterns to include in your automated trading
        systems.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Candle Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Hammer = <b>On</b>
              </li>
              <li>
                Hanging Man = <b>On</b>
              </li>
              <li>
                Bullish Belt Hold = <b>On</b>
              </li>
              <li>
                Bearish Belt Hold = <b>On</b>
              </li>
              <li>
                Bullish Engulfing = <b>On</b>
              </li>
              <li>
                Bearish Engulfing = <b>On</b>
              </li>
              <li>
                Bullish Harami = <b>On</b>
              </li>
              <li>
                Bearish Harami = <b>On</b>
              </li>
              <li>
                Bullish Harami Cross = <b>On</b>
              </li>
              <li>
                Bearish Harami Cross = <b>On</b>
              </li>
              <li>
                Bullish Doji = <b>On</b>
              </li>
              <li>
                Bearish Doji = <b>On</b>
              </li>
              <li>
                Bullish Long Legged Doji = <b>On</b>
              </li>
              <li>
                Bearish Long Legged Doji = <b>On</b>
              </li>
              <li>
                Bullish Gravestone Doji = <b>On</b>
              </li>
              <li>
                Bearish Gravestone Doji = <b>On</b>
              </li>
              <li>
                Bullish Dragonfly Doji = <b>On</b>
              </li>
              <li>
                Bearish Dragonfly Doji = <b>On</b>
              </li>
              <li>
                Dark Cloud Cover = <b>On</b>
              </li>
              <li>
                Piercing Pattern = <b>On</b>
              </li>
              <li>
                Morning Star = <b>On</b>
              </li>
              <li>
                Evening Star = <b>On</b>
              </li>
              <li>
                Bullish Inside Days = <b>On</b>
              </li>
              <li>
                Bearish Inside Days = <b>On</b>
              </li>
              <li>
                Signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Value analysis = <b>On</b>
              </li>
              <li>
                Value analysis refresh interval = <b>1000</b>
              </li>
              <li>
                Value analysis news period = <b>0</b>
              </li>
              <li>
                Value analysis indicator period = <b>0</b>
              </li>
              <li>
                Value analysis chart analyzer period = <b>0</b>
              </li>
              <li>
                Value analysis Candle pattern search period = <b>10000</b>
              </li>
              <li>
                Value analysis Chart pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis Fibonacci pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis histogram bins = <b>100</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module4950/example_image_1.png",
        description: "Expected behavior:",
        height: "260px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3322.txt",
    name: "Demonstration of Functionality | Search Engines | Example N2: Non-traded signals generated by pattern search",
    description: (
      <>
        This example showcases the chart pattern search engine operating in signal-only mode. The module identifies complex chart formations without
        placing trades, making it ideal for pattern recognition training and strategy development. It detects 18 different chart patterns including
        Wedges, Triangles, Head and Shoulders formations, Double/Triple Tops and Bottoms, and Rectangles.
        <br />
        <br />
        The system continuously scans price movements to identify these classical technical analysis patterns. When detected, visual signals appear on
        your chart, helping you understand pattern formation in real-time. The Data Feedback section collects pattern occurrence data over 10,000
        periods, providing comprehensive statistics about pattern frequency and reliability.
        <br />
        <br />
        This setup is particularly useful for traders learning to recognize chart patterns or validating pattern-based strategies. The visualization
        features help you see exactly when and where patterns form, while the statistical analysis reveals which patterns appear most frequently in
        your selected markets. This knowledge forms the foundation for developing profitable pattern-based trading strategies.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Bullish Wedge = <b>On</b>
              </li>
              <li>
                Bearish Wedge = <b>On</b>
              </li>
              <li>
                Bullish Trendchannel = <b>On</b>
              </li>
              <li>
                Bearish Trendchannel = <b>On</b>
              </li>
              <li>
                Bullish Triangle = <b>On</b>
              </li>
              <li>
                Bearish Triangle = <b>On</b>
              </li>
              <li>
                Shoulder Head Shoulder Top = <b>On</b>
              </li>
              <li>
                Shoulder Head Shoulder Bottom = <b>On</b>
              </li>
              <li>
                Double Top = <b>On</b>
              </li>
              <li>
                Double Bottom = <b>On</b>
              </li>
              <li>
                Triple Top = <b>On</b>
              </li>
              <li>
                Triple Bottom = <b>On</b>
              </li>
              <li>
                Rectangle Top = <b>On</b>
              </li>
              <li>
                Rectangle Bottom = <b>On</b>
              </li>
              <li>
                Bullish Symetric Triangle = <b>On</b>
              </li>
              <li>
                Bearish Symetric Triangle = <b>On</b>
              </li>
              <li>
                Bullish Gap = <b>On</b>
              </li>
              <li>
                Bearish Gap = <b>On</b>
              </li>
              <li>
                Signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Value analysis = <b>On</b>
              </li>
              <li>
                Value analysis refresh interval = <b>1000</b>
              </li>
              <li>
                Value analysis news period = <b>0</b>
              </li>
              <li>
                Value analysis indicator period = <b>0</b>
              </li>
              <li>
                Value analysis chart analyzer period = <b>0</b>
              </li>
              <li>
                Value analysis Candle pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis Chart pattern search period = <b>10000</b>
              </li>
              <li>
                Value analysis Fibonacci pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis histogram bins = <b>100</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3322/example_image_1.png",
        description: "Expected behavior:",
        height: "346px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1788.txt",
    name: "Demonstration of Functionality | Search Engines | Example N3: Non-traded signals generated by fibonacci search",
    description: (
      <>
        This example illustrates the Fibonacci pattern search engine functioning in observation mode. The module identifies Fibonacci retracement and
        extension patterns without executing trades, allowing traders to study these mathematical relationships in price movements. It focuses on
        detecting both bullish and bearish Fibonacci patterns based on the golden ratio levels.
        <br />
        <br />
        The search engine analyzes price swings and automatically identifies when prices respect key Fibonacci levels like 38.2%, 50%, and 61.8%.
        These levels often act as support and resistance zones where price reversals occur. The Data Feedback component tracks Fibonacci pattern
        occurrences over 10,000 periods, building a comprehensive database of pattern behavior.
        <br />
        <br />
        This configuration helps traders understand how Fibonacci mathematics applies to market movements. By observing these patterns without trading
        risk, you can evaluate their effectiveness in your chosen markets. The histogram analysis with 100 bins provides detailed insights into
        pattern distribution, helping you determine optimal settings for incorporating Fibonacci patterns into automated trading strategies.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Fibonacci Patterns
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Bullish Fibonacci = <b>On</b>
              </li>
              <li>
                Bearish Fibonacci = <b>On</b>
              </li>
              <li>
                Signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Value analysis = <b>On</b>
              </li>
              <li>
                Value analysis refresh interval = <b>1000</b>
              </li>
              <li>
                Value analysis news period = <b>0</b>
              </li>
              <li>
                Value analysis indicator period = <b>0</b>
              </li>
              <li>
                Value analysis chart analyzer period = <b>0</b>
              </li>
              <li>
                Value analysis Candle pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis Chart pattern search period = <b>0</b>
              </li>
              <li>
                Value analysis Fibonacci pattern search period = <b>10000</b>
              </li>
              <li>
                Value analysis histogram bins = <b>100</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1788/example_image_1.png",
        description: "Expected behavior:",
        height: "375px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Chart Analyzer
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1865.txt",
    name: "Demonstration of Functionality | Chart Analyzer | Example O1: Extended swing analysis",
    description: (
      <>
        This example demonstrates extended swing analysis for pattern detection using the chart analyzer. By examining the previous swing with
        specific height and width parameters, the system identifies complex formations invisible to simple indicators. The 160-180 height range
        targets swings of specific proportions.
        <br />
        <br />
        Extended swing analysis mimics human chart reading by recognizing visual patterns. Each swing's characteristics are evaluated against defined
        criteria, enabling detection of double tops, triangles, and other formations. The visual display shows detected swings and patterns clearly.
        <br />
        <br />
        This sophisticated approach bridges manual and automated analysis. The system identifies patterns consistently while maintaining the nuanced
        recognition that makes human analysis valuable.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Signal visualization = <b>Off</b>
              </li>
              <li>
                Analyzed width = <b>50</b>
              </li>
              <li>
                Draw boundaries = <b>On</b>
              </li>
              <li>
                Draw extrema = <b>On</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>On</b>
              </li>
              <li>
                Swing configuration 1
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Swing number = <b>1</b>
                  </li>
                  <li>
                    Height minimum = <b>160</b>
                  </li>
                  <li>
                    Height maximum = <b>180</b>
                  </li>
                  <li>
                    Width minimum = <b>0</b>
                  </li>
                  <li>
                    Width maximum = <b>10000</b>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1865/example_image_1.png",
        description: "Expected behavior:",
        height: "312px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module7870.txt",
    name: "Demonstration of Functionality | Chart Analyzer | Example O2: Drawing of chart analyzer panel",
    description: (
      <>
        This example displays the chart analyzer panel showing comprehensive market analysis in real-time. Support and resistance levels are
        automatically identified and displayed, providing constant market structure awareness. The panel updates continuously.
        <br />
        <br />
        The analyzer panel consolidates multiple analysis types in one display, showing volatility, swing phase, and key levels simultaneously. This
        comprehensive view helps traders understand current market context at a glance. Automatic level detection eliminates subjective bias in
        identifying important prices.
        <br />
        <br />
        Visual feedback enhances strategy understanding by showing exactly what the system sees. This transparency builds confidence in automated
        decisions while educating traders about market structure principles.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Draw boundaries = <b>Off</b>
              </li>
              <li>
                Draw extrema = <b>Off</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>Off</b>
              </li>
              <li>
                Supports and resistances forward = <b>ALL</b>
              </li>
              <li>
                Support multiplier = <b>1.00</b>
              </li>
              <li>
                Resistance multiplier = <b>1.00</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00250000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00300000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Miscellaneous
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw chart analyzer panel = <b>On</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module7870/example_image_1.png",
        description: "Expected behavior:",
        height: "427px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1452.txt",
    name: "Demonstration of Functionality | Chart Analyzer | Example O3: Exclusion of support and resistance when forwarding",
    description: (
      <>
        This example excludes certain support and resistance levels from forwarding using the e.g. -2;+2 configuration. Only the second-nearest
        support and resistance levels are forwarded for stop loss and take profit calculations. This selective forwarding prevents using levels too
        close or too far from current price.
        <br />
        <br />
        Selective level forwarding improves exit placement by filtering inappropriate levels. The nearest levels might be too close for proper risk
        management, while distant levels might be unrealistic.
        <br />
        <br />
        The visual display shows all detected levels while highlighting which ones are actively forwarded. This transparency helps traders understand
        how the system selects exit levels from available options.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Main line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Main line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators (MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>MACD</b>
              </li>
              <li>
                Line index = <b>Signal line</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (MACD Main line & MACD Signal line)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>MACD (Main line)</b>
              </li>
              <li>
                Input indicator 2 = <b>MACD (Signal line)</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Draw boundaries = <b>Off</b>
              </li>
              <li>
                Draw extrema = <b>Off</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>Off</b>
              </li>
              <li>
                Supports and resistances forward = <b>-2;+2</b>
              </li>
              <li>
                Support multiplier = <b>1.00</b>
              </li>
              <li>
                Resistance multiplier = <b>1.00</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00250000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Support/resistance from chart analyzer</b>
              </li>
              <li>
                Used chart analyzer = <b>Chart Analyzer</b>
              </li>
              <li>
                Minimum value in fix pips = <b>Off</b>
              </li>
              <li>
                Minimum value in rate of change = <b>0.00300000</b>
              </li>
              <li>
                Maximum value in fix pips = <b>Off</b>
              </li>
              <li>
                Maximum value in rate of change = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1452/example_image_1.png",
        description: "Expected behavior:",
        height: "416px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module1192.txt",
    name: "Demonstration of Functionality | Chart Analyzer | Example O4: Two chart analyzers for signal generation",
    description: (
      <>
        This example employs two chart analyzers with different parameters for comprehensive signal generation. The 50-bar analyzer detects short-term
        volatility while the 200-bar analyzer identifies longer-term market phases. Combining both provides multi-timeframe analysis within a single
        timeframe.
        <br />
        <br />
        Different analyzer widths capture different market characteristics. Short analyzers respond quickly to recent changes while long analyzers
        identify persistent conditions. The combination requires agreement from both perspectives, filtering signals effectively.
        <br />
        <br />
        Dual analyzer strategies demonstrate how multiple analysis windows can work together. This approach applies to any indicator or analysis
        method where different lookback periods provide complementary insights.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer 1
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Analyzed width = <b>50</b>
              </li>
              <li>
                Draw boundaries = <b>On</b>
              </li>
              <li>
                Draw extrema = <b>On</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>On</b>
              </li>
              <li>
                Signal if volatility is low = <b>Off</b>
              </li>
              <li>
                Signal if volatility is middle = <b>Off</b>
              </li>
              <li>
                Signal if volatility is high = <b>On</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Chart Analyzer 2
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Analyzed width = <b>200</b>
              </li>
              <li>
                Draw boundaries = <b>On</b>
              </li>
              <li>
                Draw extrema = <b>On</b>
              </li>
              <li>
                Draw supports and resistances = <b>On</b>
              </li>
              <li>
                Draw swing phase = <b>On</b>
              </li>
              <li>
                Signal if swing phase is overstatement = <b>Off</b>
              </li>
              <li>
                Signal if swing phase is understatement = <b>On</b>
              </li>
              <li>
                Signal if swing phase is over correction = <b>Off</b>
              </li>
              <li>
                Signal if swing phase is under correction = <b>On</b>
              </li>
              <li>
                Signal if swing phase is upswing = <b>Off</b>
              </li>
              <li>
                Signal if swing phase is downswing = <b>Off</b>
              </li>
              <li>
                Signal if swing phase is undefined = <b>Off</b>
              </li>
              <li>
                Breakout factor = <b>1.00</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module1192/example_image_1.png",
        description: "Expected behavior:",
        height: "302px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Data Feedback
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9020.txt",
    name: "Demonstration of Functionality | Data Feedback | Example P1: All feedback features enabled",
    description: (
      <>
        This example enables all data feedback features for comprehensive strategy analysis. Every aspect of trading activity is recorded - from tick
        data to performance metrics. The inspector continuously monitors strategy health while detailed logs track all events.
        <br />
        <br />
        Comprehensive feedback enables deep strategy analysis and optimization. By recording everything, traders can identify patterns, problems, and
        opportunities invisible during live trading. The multi-timeframe symbol data provides context for trade decisions.
        <br />
        <br />
        This data-rich approach suits serious strategy development where every detail matters. The collected information enables backtesting
        validation, performance analysis, and systematic improvement based on empirical evidence.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Gradient condition = <b>Cross Limit 1</b>
              </li>
              <li>
                Gradient limit 1 = <b>0.00000000</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Inspector = <b>On</b>
              </li>
              <li>
                Present trades = <b>On</b>
              </li>
              <li>
                Past trades = <b>On</b>
              </li>
              <li>
                Meta data = <b>On</b>
              </li>
              <li>
                Trade screenshots = <b>Off</b>
              </li>
              <li>
                Current value = <b>On</b>
              </li>
              <li>
                Current value news = <b>On</b>
              </li>
              <li>
                Current value indicator = <b>On</b>
              </li>
              <li>
                Current value chart analyzer = <b>On</b>
              </li>
              <li>
                Current value Candle pattern search = <b>On</b>
              </li>
              <li>
                Current value Chart pattern search = <b>On</b>
              </li>
              <li>
                Current value Fibonacci pattern search = <b>On</b>
              </li>
              <li>
                Time analysis = <b>On</b>
              </li>
              <li>
                Value analysis = <b>On</b>
              </li>
              <li>
                Signal analysis = <b>On</b>
              </li>
              <li>
                Symbol series bars current = <b>On</b>
              </li>
              <li>
                Symbol series bars completed = <b>On</b>
              </li>
              <li>
                Symbol series M1 = <b>On</b>
              </li>
              <li>
                Symbol series M5 = <b>On</b>
              </li>
              <li>
                Symbol series M15 = <b>On</b>
              </li>
              <li>
                Symbol series M30 = <b>On</b>
              </li>
              <li>
                Symbol series H1 = <b>On</b>
              </li>
              <li>
                Symbol series H4 = <b>On</b>
              </li>
              <li>
                Symbol series D1 = <b>On</b>
              </li>
              <li>
                Symbol series W1 = <b>On</b>
              </li>
              <li>
                Symbol series MN = <b>On</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9020/example_image_1.png",
        description: "Expected behavior:",
        height: "630px",
      },
      {
        src: "/illustrations/converted/examples/Module9020/example_image_2.png",
        description: "Expected behavior:",
        height: "487px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Multi-Symbol Trading
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module5949.txt",
    name: "Demonstration of Functionality | Multi-Symbol Trading | Example Q1: Symbol mapping for custom symbols",
    description: (
      <>
        This example demonstrates symbol name mapping for multi-broker compatibility. The USDCHF references in the strategy are automatically replaced
        with EURUSD during execution. This enables using the same strategy across brokers with different symbol naming conventions.
        <br />
        <br />
        Symbol mapping solves practical problems when switching brokers or using custom symbols. Strategies remain portable without manual editing for
        each broker's naming scheme. The three mapping configurations show different live versus testing applications.
        <br />
        <br />
        This flexibility enables sophisticated multi-symbol strategies that adapt to available instruments. Traders can develop strategies on custom
        symbols then deploy them on standard symbols without modification.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15, M1 run in both symbols required.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 1 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>Current</b>
              </li>
              <li>
                Shift = <b>1</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 2 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>USDCHF</b>
              </li>
              <li>
                Shift = <b>0</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Symbol and Timeframe
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Symbol = <b>Current</b>
              </li>
              <li>
                Timeframe = <b>Current</b>
              </li>
              <li>
                Symbol name mapping 1
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Used in live trading = <b>On</b>
                  </li>
                  <li>
                    Used in strategy testing = <b>Off</b>
                  </li>
                  <li>
                    String to replace = <b>USDCHF</b>
                  </li>
                  <li>
                    String it is replaced with = <b>EURUSD</b>
                  </li>
                </ul>
              </li>
              <li>
                Symbol name mapping 2
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Used in live trading = <b>On</b>
                  </li>
                  <li>
                    Used in strategy testing = <b>On</b>
                  </li>
                  <li>
                    String to replace = <b>USDCHF</b>
                  </li>
                  <li>
                    String it is replaced with = <b>EURUSD</b>
                  </li>
                </ul>
              </li>
              <li>
                Symbol name mapping 3
                <ul style={{ listStyleType: "square" }}>
                  <li>
                    Used in live trading = <b>Off</b>
                  </li>
                  <li>
                    Used in strategy testing = <b>On</b>
                  </li>
                  <li>
                    String to replace = <b>USDCHF</b>
                  </li>
                  <li>
                    String it is replaced with = <b>EURUSD</b>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module5949/example_image_1.png",
        description: "Expected behavior:",
        height: "547px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module5668.txt",
    name: "Demonstration of Functionality | Multi-Symbol Trading | Example Q2: Usage of multiple symbols and symbol feedback (EURUSD and USDCHF)",
    description: (
      <>
        This example uses multiple symbols simultaneously with comprehensive feedback showing both EURUSD and USDCHF data. The correlation between
        symbols is visible as oscillator, helping understand inter-market relationships. Both symbols must have M1 data available for proper
        operation.
        <br />
        <br />
        Multi-symbol strategies capture relationships between correlated instruments. By monitoring multiple symbols, the system can identify
        divergences and confirmations across markets. The symbol series display shows price action alignment between markets.
        <br />
        <br />
        This approach enables pairs trading, hedging, and correlation-based strategies. Understanding how different symbols interact provides insights
        unavailable from single-symbol analysis.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15, M1 run in both symbols required.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 1 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>Current</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 2 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>USDCHF</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Inspector = <b>On</b>
              </li>
              <li>
                Symbol series bars current = <b>On</b>
              </li>
              <li>
                Symbol series bars completed = <b>On</b>
              </li>
              <li>
                Symbol series M1 = <b>Off</b>
              </li>
              <li>
                Symbol series M5 = <b>Off</b>
              </li>
              <li>
                Symbol series M15 = <b>On</b>
              </li>
              <li>
                Symbol series M15 range = <b>100</b>
              </li>
              <li>
                Symbol series M30 = <b>Off</b>
              </li>
              <li>
                Symbol series H1 = <b>Off</b>
              </li>
              <li>
                Symbol series H4 = <b>Off</b>
              </li>
              <li>
                Symbol series D1 = <b>Off</b>
              </li>
              <li>
                Symbol series W1 = <b>Off</b>
              </li>
              <li>
                Symbol series MN = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module5668/example_image_1.png",
        description: "Expected behavior:",
        height: "509px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module3457.txt",
    name: "Demonstration of Functionality | Multi-Symbol Trading | Example Q3: Correlation coefficient between two symbols (EURUSD and USDCHF)",
    description: (
      <>
        This example calculates correlation coefficients between EURUSD and USDCHF over 50 periods. The correlation indicator quantifies relationship
        strength between symbols, generating signals based on correlation changes. This mathematical approach removes subjective interpretation of
        inter-market relationships.
        <br />
        <br />
        Correlation values range from -1 to +1, with negative values indicating inverse relationships. EURUSD and USDCHF typically show negative
        correlation due to USD's opposite position in each pair. Trading correlation changes can identify relationship breakdowns offering
        opportunities.
        <br />
        <br />
        The visual correlation display helps traders understand dynamic relationships between markets. This quantitative approach provides objective
        measurement of subjective concepts like "correlation" or "divergence."
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15, M1 run in both symbols required.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - COR
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>COR</b>
              </li>
              <li>
                Period = <b>50</b>
              </li>
              <li>
                Symbol for comparison = <b>USDCHF</b>
              </li>
              <li>
                Symbol = <b>Current</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module3457/example_image_1.png",
        description: "Expected behavior:",
        height: "545px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | External Python Script integration
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module2258.txt",
    name: "Demonstration of Functionality | External Python Script integration | Example R1: Signal from custom oscillator and external Python script",
    description: (
      <>
        This example integrates external Python scripts for sophisticated signal generation. The MyExternalOscillatorGenerator.py script performs
        calculations, sending results to the custom indicator. This enables machine learning, statistical analysis, or any Python-based strategy.
        <br />
        <br />
        Python integration opens unlimited possibilities for strategy development. Modern data science tools, API connections, and complex algorithms
        become available while maintaining MetaTrader's execution capabilities. The visual display shows Python-generated signals clearly.
        <br />
        <br />
        This advanced integration bridges modern programming with traditional trading platforms. Traders can leverage Python's vast ecosystem while
        benefiting from MetaTrader's market access and execution infrastructure.
        <br />
        <br />
        <b>Please note:</b> This example requires an external oscillator and an additional Python script. Please download, compile, and use the files
        listed below. Ensure the paths are correctly configured and the Python script is running during execution.
        <br />
        <br />
        <Link href={"/files/misc/MyExternalOscillator.mq4"} download="MyExternalOscillator.mq4" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalOscillator.mq4`}
        </Link>
        <br />
        <Link href={"/files/misc/MyExternalOscillator.mq5"} download="MyExternalOscillator.mq5" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalOscillator.mq5`}
        </Link>
        <br />
        <Link href={"/files/misc/MyExternalOscillatorGenerator.py"} download="MyExternalOscillatorGenerator.py" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalOscillatorGenerator.py`}
        </Link>
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - CUS - Custom oscillator
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>CUS - Custom oscillator</b>
              </li>
              <li>
                MetaTrader4 path to indicator file = <b>\FEA Trading\MT4\MyExternalOscillator.ex4</b>
              </li>
              <li>
                MetaTrader5 path to indicator file = <b>FEA Trading\MT5\MyExternalOscillator</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module2258/example_image_1.png",
        description: "Expected behavior:",
        height: "603px",
      },
      {
        src: "/illustrations/converted/examples/Module2258/example_image_2.png",
        description: "Expected behavior:",
        height: "341px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module9553.txt",
    name: "Demonstration of Functionality | External Python Script integration | Example R2: Parameter forwarding and feedback parsing with custom indicator and external Python script",
    description: (
      <>
        This example demonstrates parameter forwarding between MetaTrader and Python with feedback parsing. Three parameters are sent to the Python
        script which processes them and returns calculated values. The feedback system displays both sent parameters and received results for complete
        transparency.
        <br />
        <br />
        Bidirectional communication enables dynamic strategy adjustment based on external calculations. Python scripts can access databases, web
        services, or complex models, returning optimized parameters for trading. The 100-bar symbol series provides market context for external
        processing.
        <br />
        <br />
        This sophisticated integration represents the future of algorithmic trading where multiple systems collaborate. Traditional indicators combine
        with modern data processing for superior strategy development.
        <br />
        <br />
        <b>Please note:</b> This example requires an external indicator and an additional Python script. Please download, compile, and use the files
        listed below. Ensure the paths are correctly configured and the Python script is running during execution.
        <br />
        <br />
        <Link href={"/files/misc/MyExternalIndicator.mq4"} download="MyExternalIndicator.mq4" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalIndicator.mq4`}
        </Link>
        <br />
        <Link href={"/files/misc/MyExternalIndicator.mq5"} download="MyExternalIndicator.mq5" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalIndicator.mq5`}
        </Link>
        <br />
        <Link href={"/files/misc/MyExternalIndicatorGenerator.py"} download="MyExternalIndicatorGenerator.py" underline="none" variant="contentMiddleLink" color="inherit">
          {`MyExternalIndicatorGenerator.py`}
        </Link>
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - CUS - Custom indicator
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>CUS - Custom indicator</b>
              </li>
              <li>
                Custom parameter 1 = <b>1.00000000</b>
              </li>
              <li>
                Custom parameter 2 = <b>2.00000000</b>
              </li>
              <li>
                Custom parameter 3 = <b>3.00000000</b>
              </li>
              <li>
                MetaTrader4 path to indicator file = <b>\FEA Trading\MT4\MyExternalIndicator.ex4</b>
              </li>
              <li>
                MetaTrader5 path to indicator file = <b>FEA Trading\MT5\MyExternalIndicator</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Data Feedback
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Symbol series bars current = <b>On</b>
              </li>
              <li>
                Symbol series bars completed = <b>On</b>
              </li>
              <li>
                Symbol series M1 = <b>Off</b>
              </li>
              <li>
                Symbol series M5 = <b>Off</b>
              </li>
              <li>
                Symbol series M15 = <b>On</b>
              </li>
              <li>
                Symbol series M15 range = <b>100</b>
              </li>
              <li>
                Symbol series M30 = <b>Off</b>
              </li>
              <li>
                Symbol series H1 = <b>Off</b>
              </li>
              <li>
                Symbol series H4 = <b>Off</b>
              </li>
              <li>
                Symbol series D1 = <b>Off</b>
              </li>
              <li>
                Symbol series W1 = <b>Off</b>
              </li>
              <li>
                Symbol series MN = <b>Off</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9553/example_image_1.png",
        description: "Expected behavior:",
        height: "403px",
      },
      {
        src: "/illustrations/converted/examples/Module9553/example_image_2.png",
        description: "Expected behavior:",
        height: "519px",
      },
    ],
  },

  // ----------------------------------------------------------------------
  // Demonstration of Functionality | Others
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION
    path: "/files/examples/Module6030.txt",
    name: "Demonstration of Functionality | Others | Example S1: Drawing of second timeframe in chart",
    description: (
      <>
        This example overlays H4 candles on faster timeframes for multi-timeframe analysis. The second timeframe display shows larger trend context
        while trading on smaller timeframes. This prevents trading against major trends invisible on fast charts.
        <br />
        <br />
        Higher timeframe overlay provides instant context without switching charts. Traders see both detailed entry timing and overall trend direction
        simultaneously. The visual representation makes multi-timeframe alignment immediately apparent.
        <br />
        <br />
        This display technique teaches important multi-timeframe concepts. Understanding how different timeframes relate helps traders avoid common
        mistakes like counter-trend trading based on short-term signals alone.
        <br />
        <br />
        <b>Please note:</b> Recommended to use timeframe smaller than H4
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - STD
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Forward</b>
              </li>
              <li>
                Selected indicator = <b>STD</b>
              </li>
              <li>
                Draw in chart = <b>Off</b>
              </li>
              <li>
                Forward adjustment = <b>3.50</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - SMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>SMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators - EMA
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>EMA</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicator Combinations (SMA & EMA)
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Input indicator 1 = <b>SMA</b>
              </li>
              <li>
                Input indicator 2 = <b>EMA</b>
              </li>
              <li>
                Value relation = <b>Value x cross value x</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Stop Loss
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Indicator</b>
              </li>
              <li>
                Used indicator = <b>STD</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Initial Take Profit
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Selection = <b>Risk ratio</b>
              </li>
              <li>
                Risk ratio = <b>1.40</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Miscellaneous
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw second timeframe = <b>H4</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module6030/example_image_1.png",
        description: "Expected behavior:",
        height: "451px",
      },
    ],
  },
  // ----------------------------------------------------------------------
  {
    // DESCRIPTION - GUIDE
    path: "/files/examples/Module9898.txt",
    name: "Demonstration of Functionality | Others | Example S2: Drawing of unequal symbols",
    description: (
      <>
        This example displays unequal symbols on the same chart when indicators use different symbols. Both EURUSD and USDCHF appear together, showing
        their inverse relationship visually. This unique display helps understand how multi-symbol strategies operate.
        <br />
        <br />
        Unequal symbol display reveals correlation and divergence patterns immediately. When symbols that normally move inversely begin moving
        together, significant opportunities may exist. The visual overlay eliminates constant chart switching for multi-symbol monitoring.
        <br />
        <br />
        This advanced visualization suits sophisticated strategies using multiple instruments. Seeing all relevant symbols simultaneously improves
        decision-making and pattern recognition across markets.
        <br />
        <br />
        <b>Please note:</b> Recommended to use EURUSD M15, M1 run in both symbols required.
        <br />
        <br />
        You can either import this example directly or set it up manually. To manually replicate the trading module, make sure the{" "}
        <b>newly added module</b> includes the following key settings and that the <b>professional mode is activated</b>:
        <ul style={{ listStyleType: "disc" }}>
          <li style={{ marginBottom: "10px" }}>
            Trade Basics
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Operation type = <b>Long signals only drawn</b>
              </li>
              <li>
                General signal visualization = <b>Off</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 1 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>Current</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>1</b>
              </li>
            </ul>
          </li>
          <li style={{ marginBottom: "10px" }}>
            Signal Generation - Indicators 2 - RSI
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Signal in use = <b>On</b>
              </li>
              <li>
                Signal used for = <b>Entry</b>
              </li>
              <li>
                Selected indicator = <b>RSI</b>
              </li>
              <li>
                Symbol = <b>USDCHF</b>
              </li>
              <li>
                Draw in chart = <b>On</b>
              </li>
              <li>
                Drawing slot = <b>2</b>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Miscellaneous
            <ul style={{ listStyleType: "circle" }}>
              <li>
                Draw unequal symbols = <b>On</b>
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
    images: [
      {
        src: "/illustrations/converted/examples/Module9898/example_image_1.png",
        description: "Expected behavior:",
        height: "419px",
      },
    ],
  },
];

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleAddExampleModule({ onAddExample, existingModules = [], maxModules = 200 }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleAddExampleModule.propTypes = {
    onAddExample: PropTypes.func.isRequired,
    existingModules: PropTypes.array,
    maxModules: PropTypes.number,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const [selectedExample, setSelectedExample] = useState("");
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);
  const [scroll, setScroll] = useState("paper");
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setSelectedExample("");
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    setIsSubmitting(false);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getSelectedExampleData = () => exampleModules.find((example) => example.path === selectedExample);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const readExampleFile = async (filePath) => {
    try {
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      const txtData = await response.text();
      return txtData;
    } catch (errorInner) {
      // eslint-disable-next-line
      console.error("Error reading example file:", errorInner);
      throw errorInner;
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleAddExample = async () => {
    if (!selectedExample) {
      setWarning(true);
      setReturnMessage("Please select an example module");
      return;
    }

    // Check if module limit is reached
    if (existingModules.length >= maxModules) {
      setError(true);
      setReturnMessage(`Cannot add module: The maximum limit of ${maxModules} modules has already been reached.`);
      return;
    }

    setIsSubmitting(true);

    try {
      setError(false);
      setWarning(false);
      setSuccess(false);
      setReturnMessage("");

      const exampleData = getSelectedExampleData();

      // Read the file content from the path
      const txtData = await readExampleFile(exampleData.path);

      // Convert the TXT data to module data
      const moduleData = TxtDataToModuleData(txtData, 0);

      // Add additional metadata from the example
      const enrichedModuleData = {
        ...moduleData,
        isExample: true,
        exampleMetadata: {
          path: exampleData.path,
          name: exampleData.name,
          description: exampleData.description,
        },
      };

      onAddExample(enrichedModuleData);

      setSuccess(true);
      setReturnMessage(`Example module "${exampleData.name}" added successfully!`);

      // autoCloseTimeoutRef.current = setTimeout(() => {
      //  handleClose();
      // }, 1500);
    } catch (err) {
      // eslint-disable-next-line
      console.error("Error adding example module:", err);
      setError(true);
      setReturnMessage(`Failed to load example module: ${err.message}`);
    }

    setIsSubmitting(false);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    // Get the persisted value from localStorage or use default
    const persistedExample = localStorage.getItem("selectedExampleModule");
    const defaultExample = "/files/examples/Module8103.txt";

    // Set the selected example: use persisted value if it exists, otherwise use default
    if (persistedExample && exampleModules.some((module) => module.path === persistedExample)) {
      setSelectedExample(persistedExample);
    } else {
      setSelectedExample(defaultExample);
    }

    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const selectedExampleData = getSelectedExampleData();

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Add example module
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Add example module</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
                px: 2,
                pt: 2,
                pb: 1,
                width: "100%",
                boxSizing: "border-box",
                "&::-webkit-scrollbar": {
                  width: "16px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#e0e0e0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888888",
                  borderRadius: "6px",
                  border: "2px solid #e0e0e0",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#666666",
                },
                // Firefox scrollbar styling
                scrollbarWidth: "thin",
                scrollbarColor: "#888888 #e0e0e0",
              }}
            >
              <Stack spacing={2}>
                {existingModules.length >= maxModules && (
                  <Box sx={{ m: -1 }}>
                    <ModalAlertError severity="error">
                      Maximum module limit reached ({existingModules.length}/{maxModules}). You cannot add any more modules until you remove some
                      existing ones.
                    </ModalAlertError>
                  </Box>
                )}

                {existingModules.length < maxModules && existingModules.length >= maxModules - 10 && (
                  <Box sx={{ m: -1 }}>
                    <ModalAlertWarning severity="warning">
                      You have {existingModules.length} of {maxModules} modules. Only {maxModules - existingModules.length} more can be added.
                    </ModalAlertWarning>
                  </Box>
                )}

                <Box>
                  <Typography variant="contentMiddle">
                    <b>Please note:</b> These are not ready-to-use trading strategies. Each strategy requires adjustment to the timeframe and symbol,
                    along with thorough backtesting. The examples are intended as a foundation for your own custom strategy and to help you get
                    familiar with the Expert Advisor Builder.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="contentMiddle">
                    Select an example from the dropdown menu and click <b>Add example module</b>. Once added, you can export and run it in MetaTrader
                    as usual.
                  </Typography>
                </Box>
                <Box>
                  <SmartParameter value="Select example module" helpReference="HandleAddExampleModule" helpText="" helpLink="" fieldName="Select" />
                  <FormControl fullWidth>
                    <Select
                      value={selectedExample}
                      onChange={(event) => {
                        const newValue = event.target.value;
                        setSelectedExample(newValue);
                        // Persist the selection to localStorage
                        localStorage.setItem("selectedExampleModule", newValue);
                      }}
                      input={<ModalContentOutlinedInput />}
                    >
                      {exampleModules.map((example) => (
                        <ModalContentMenuItem
                          key={example.path}
                          value={example.path}
                          sx={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            minHeight: "auto",
                            height: "auto",
                            padding: "8px 16px",
                          }}
                        >
                          {example.name}
                        </ModalContentMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {selectedExampleData && (
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="contentMiddle">{selectedExampleData.description}</Typography>
                    </Box>

                    {selectedExampleData.images && selectedExampleData.images.length > 0 && (
                      <Stack spacing={2}>
                        {selectedExampleData.images.map((imageData, index) => (
                          <Box key={index}>
                            {imageData.description && <Typography variant="contentMiddle">{imageData.description}</Typography>}
                            <Box
                              sx={{
                                borderRadius: "10px",
                                mt: 1,
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "#DCDCDC",
                              }}
                              key={index}
                            >
                              <CardMedia
                                component="img"
                                height="auto"
                                image={imageData.src}
                                alt={imageData.alt}
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: imageData.height,
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </DialogContent>

            <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
              <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
            </Box>

            <DialogActions
              sx={{
                background: "#F2F2F2",
                p: 2,
                pt: 0,
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleAddExample}
                  loading={isSubmitting}
                  disabled={!selectedExample || existingModules.length >= maxModules}
                  sx={{ mt: 1 }}
                >
                  Add example module
                </ModalActionsLoadingButton>

                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleClose}
                  loading={isSubmitting}
                  sx={{ mt: 1 }}
                >
                  Close
                </ModalActionsLoadingButton>
              </Box>
            </DialogActions>
          </>
        }
      />
    </>
  );
}
